/**
 * Host Global - Property Rental Management System
 * Property Owner - Add Property Page
 * Professional form design with validation and file uploads
 */

import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";

// @mui material components
import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";
import TextField from "@mui/material/TextField";
import FormControl from "@mui/material/FormControl";
import InputLabel from "@mui/material/InputLabel";
import Select from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";
import Chip from "@mui/material/Chip";
import Box from "@mui/material/Box";
import CircularProgress from "@mui/material/CircularProgress";
import Backdrop from "@mui/material/Backdrop";
import Alert from "@mui/material/Alert";
import IconButton from "@mui/material/IconButton";
import Paper from "@mui/material/Paper";
import Autocomplete from "@mui/material/Autocomplete";
import Stepper from "@mui/material/Stepper";
import Step from "@mui/material/Step";
import StepLabel from "@mui/material/StepLabel";
import StepContent from "@mui/material/StepContent";
import FormControlLabel from "@mui/material/FormControlLabel";
import Switch from "@mui/material/Switch";
import LinearProgress from "@mui/material/LinearProgress";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";

// Components
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";

// Layout components
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import PropertyOwnerFooter from "examples/PropertyOwnerFooter";

// API
import { propertyOwnerAPI } from "utils/api";

// Icons
import Icon from "@mui/material/Icon";

const PROPERTY_TYPES = [
  {
    value: "villa",
    label: "Villa",
    icon: "villa",
    description: "Luxury standalone property",
  },
  {
    value: "apartment",
    label: "Apartment",
    icon: "apartment",
    description: "Unit in a multi-story building",
  },
  {
    value: "vacation home",
    label: "Vacation Home",
    icon: "holiday_village",
    description: "Holiday rental property",
  },
  {
    value: "studio",
    label: "Studio",
    icon: "meeting_room",
    description: "Single room living space",
  },
  {
    value: "townhome",
    label: "Townhome",
    icon: "home_work",
    description: "Multi-floor attached home",
  },
  {
    value: "house",
    label: "House",
    icon: "home",
    description: "Standalone family home",
  },
];

// Amenities with Arabic translations
const COMMON_AMENITIES = [
  { en: "WiFi", ar: "واي فاي" },
  { en: "Air Conditioning", ar: "تكييف هواء" },
  { en: "Heating", ar: "تدفئة" },
  { en: "Kitchen", ar: "مطبخ" },
  { en: "Washer", ar: "غسالة" },
  { en: "Dryer", ar: "مجفف" },
  { en: "Parking", ar: "موقف سيارات" },
  { en: "Pool", ar: "مسبح" },
  { en: "Gym", ar: "صالة ألعاب رياضية" },
  { en: "Balcony", ar: "شرفة" },
  { en: "Garden", ar: "حديقة" },
  { en: "Pet Friendly", ar: "صديق للحيوانات الأليفة" },
  { en: "Smoking Allowed", ar: "التدخين مسموح" },
  { en: "TV", ar: "تلفزيون" },
  { en: "Cable TV", ar: "تلفزيون كابل" },
  { en: "Hot Tub", ar: "جاكوزي" },
  { en: "Fireplace", ar: "مدفأة" },
  { en: "Elevator", ar: "مصعد" },
  { en: "Security System", ar: "نظام أمني" },
  { en: "Wheelchair Accessible", ar: "متاح لكرسي متحرك" },
];

// Compound Facilities with Arabic translations
const COMPOUND_FACILITIES = [
  { en: "Swimming Pool", ar: "مسبح" },
  { en: "Gym/Fitness Center", ar: "صالة ألعاب رياضية" },
  { en: "Playground", ar: "ملعب أطفال" },
  { en: "Parking", ar: "موقف سيارات" },
  { en: "Security", ar: "أمن" },
  { en: "Garden", ar: "حديقة" },
  { en: "BBQ Area", ar: "منطقة شواء" },
  { en: "Clubhouse", ar: "نادي اجتماعي" },
  { en: "Tennis Court", ar: "ملعب تنس" },
  { en: "Basketball Court", ar: "ملعب كرة سلة" },
  { en: "Football Field", ar: "ملعب كرة قدم" },
  { en: "Running Track", ar: "مسار جري" },
  { en: "Kids Play Area", ar: "منطقة لعب للأطفال" },
  { en: "Laundry Room", ar: "غرفة غسيل" },
  { en: "Storage Units", ar: "وحدات تخزين" },
  { en: "Elevator", ar: "مصعد" },
  { en: "24/7 Security", ar: "أمن على مدار الساعة" },
  { en: "CCTV", ar: "كاميرات مراقبة" },
  { en: "Gated Community", ar: "مجتمع مسور" },
  { en: "Landscaped Gardens", ar: "حدائق منسقة" },
  { en: "Reception Area", ar: "منطقة استقبال" },
  { en: "Business Center", ar: "مركز أعمال" },
  { en: "Library", ar: "مكتبة" },
  { en: "Cinema Room", ar: "قاعة سينما" },
  { en: "Spa", ar: "سبا" },
  { en: "Restaurant", ar: "مطعم" },
  { en: "Café", ar: "مقهى" },
  { en: "Supermarket", ar: "سوبر ماركت" },
  { en: "Pharmacy", ar: "صيدلية" },
  { en: "ATM", ar: "صراف آلي" },
];

// Criteria Arabic translations
const CRITERIA_TRANSLATIONS = {
  // Bedroom
  "Bed 1.2 meter": "سرير 1.2 متر",
  "Bed 1.5 meter": "سرير 1.5 متر",
  "Bed 1.8 meter (King)": "سرير 1.8 متر (كينج)",
  "Single Bed": "سرير فردي",
  "Double Bed": "سرير مزدوج",
  "Bunk Beds": "أسرة متعددة الطوابق",
  Wardrobe: "خزانة ملابس",
  Dresser: "خزانة أدراج",
  Nightstand: "منضدة جانبية",
  Mirror: "مرآة",

  // Bathroom
  Shower: "دش",
  Bathtub: "حوض استحمام",
  "Hot Water": "ماء ساخن",
  "Towel Rack": "حامل المناشف",
  "Toilet Paper Holder": "حامل ورق التواليت",
  "Mirror in Bathroom": "مرآة في الحمام",
  "Hair Dryer": "مجفف شعر",

  // Kitchen
  Refrigerator: "ثلاجة",
  Stove: "موقد",
  Oven: "فرن",
  Microwave: "ميكروويف",
  Dishwasher: "غسالة أطباق",
  "Coffee Maker": "آلة صنع القهوة",
  Kettle: "غلاية",
  "Cooking Utensils": "أدوات الطهي",
  "Dishes & Cutlery": "أطباق وأدوات المائدة",
  "Dining Table": "طاولة طعام",
  Chairs: "كراسي",

  // Living Area
  Sofa: "أريكة",
  TV: "تلفزيون",
  "Coffee Table": "طاولة قهوة",
  Bookshelf: "رف كتب",
  "Curtains/Blinds": "ستائر/مظلات",

  // Amenities & Facilities
  "Swimming Pool": "مسبح",
  "Pool Access": "وصول للمسبح",
  "Gym/Fitness Center": "صالة ألعاب رياضية",
  "Parking Space": "موقف سيارات",
  Balcony: "شرفة",
  Terrace: "تراس",
  Garden: "حديقة",
  "BBQ Area": "منطقة شواء",
  Elevator: "مصعد",
  "Security System": "نظام أمني",
  CCTV: "كاميرات مراقبة",

  // Technology
  WiFi: "واي فاي",
  "High-Speed Internet": "إنترنت عالي السرعة",
  "Cable TV": "تلفزيون كابل",
  "Smart TV": "تلفزيون ذكي",
  "Air Conditioning": "تكييف هواء",
  "Central Heating": "تدفئة مركزية",
  "Washing Machine": "غسالة",
  Dryer: "مجفف",

  // Safety & Accessibility
  "Smoke Detector": "كاشف الدخان",
  "Fire Extinguisher": "طفاية حريق",
  "First Aid Kit": "صندوق إسعافات أولية",
  "Wheelchair Accessible": "متاح لكرسي متحرك",
  "Ramp Access": "وصول بممر منحدر",

  // Additional
  "Iron & Ironing Board": "مكواة وطاولة كي",
  "Vacuum Cleaner": "مكنسة كهربائية",
  "Linen Provided": "أغطية متوفرة",
  "Towels Provided": "مناشف متوفرة",
  "Pet Friendly": "صديق للحيوانات الأليفة",
};

// Category Arabic translations
const CATEGORY_TRANSLATIONS = {
  Bedroom: "غرفة نوم",
  Bathroom: "حمام",
  Kitchen: "مطبخ",
  "Living Area": "منطقة المعيشة",
  Amenities: "مرافق",
  Technology: "تقنية",
  Safety: "سلامة",
  Accessibility: "إمكانية الوصول",
  Additional: "إضافي",
  General: "عام",
};

// Property Criteria Checklist - Now fetched from API
// Removed static PROPERTY_CRITERIA array - using API data instead

// Countries list
const COUNTRIES = [
  "Egypt",
  "United States",
  "United Kingdom",
  "Canada",
  "Australia",
  "Germany",
  "France",
  "Italy",
  "Spain",
  "Netherlands",
  "Belgium",
  "Switzerland",
  "Austria",
  "Sweden",
  "Norway",
  "Denmark",
  "Finland",
  "Poland",
  "Portugal",
  "Greece",
  "Turkey",
  "United Arab Emirates",
  "Saudi Arabia",
  "Qatar",
  "Kuwait",
  "Bahrain",
  "Oman",
  "Jordan",
  "Lebanon",
  "Morocco",
  "Tunisia",
  "Algeria",
  "South Africa",
  "Kenya",
  "Nigeria",
  "India",
  "China",
  "Japan",
  "South Korea",
  "Singapore",
  "Malaysia",
  "Thailand",
  "Indonesia",
  "Philippines",
  "Vietnam",
  "Brazil",
  "Argentina",
  "Mexico",
  "Chile",
  "Colombia",
  "Peru",
  "New Zealand",
  "Ireland",
  "Iceland",
  "Luxembourg",
  "Czech Republic",
  "Hungary",
  "Romania",
  "Bulgaria",
  "Croatia",
  "Serbia",
  "Slovenia",
  "Slovakia",
  "Estonia",
  "Latvia",
  "Lithuania",
  "Cyprus",
  "Malta",
  "Israel",
  "Russia",
  "Ukraine",
  "Belarus",
  "Kazakhstan",
  "Uzbekistan",
  "Azerbaijan",
  "Georgia",
  "Armenia",
  "Moldova",
  "Albania",
  "Bosnia and Herzegovina",
  "Macedonia",
  "Montenegro",
  "Kosovo",
  "Andorra",
  "Monaco",
  "Liechtenstein",
  "San Marino",
  "Vatican City",
  "Iraq",
  "Iran",
  "Pakistan",
  "Bangladesh",
  "Sri Lanka",
  "Nepal",
  "Bhutan",
  "Myanmar",
  "Cambodia",
  "Laos",
  "Mongolia",
  "Taiwan",
  "Hong Kong",
  "Macau",
  "Brunei",
  "East Timor",
  "Papua New Guinea",
  "Fiji",
  "Samoa",
  "Tonga",
  "Vanuatu",
  "Solomon Islands",
  "Maldives",
  "Seychelles",
  "Mauritius",
  "Madagascar",
  "Tanzania",
  "Uganda",
  "Rwanda",
  "Ethiopia",
  "Ghana",
  "Senegal",
  "Ivory Coast",
  "Cameroon",
  "Angola",
  "Mozambique",
  "Zimbabwe",
  "Botswana",
  "Namibia",
  "Zambia",
  "Malawi",
  "Eswatini",
  "Lesotho",
  "Gabon",
  "Congo",
  "Chad",
  "Niger",
  "Mali",
  "Burkina Faso",
  "Guinea",
  "Sierra Leone",
  "Liberia",
  "Togo",
  "Benin",
  "Gambia",
  "Guinea-Bissau",
  "Cape Verde",
  "São Tomé and Príncipe",
  "Equatorial Guinea",
  "Central African Republic",
  "Sudan",
  "South Sudan",
  "Eritrea",
  "Djibouti",
  "Somalia",
  "Burundi",
  "Comoros",
  "Libya",
  "Mauritania",
  "Western Sahara",
  "Uruguay",
  "Paraguay",
  "Bolivia",
  "Ecuador",
  "Venezuela",
  "Guyana",
  "Suriname",
  "French Guiana",
  "Belize",
  "Guatemala",
  "Honduras",
  "El Salvador",
  "Nicaragua",
  "Costa Rica",
  "Panama",
  "Cuba",
  "Jamaica",
  "Haiti",
  "Dominican Republic",
  "Trinidad and Tobago",
  "Barbados",
  "Bahamas",
  "Greenland",
  "Bermuda",
  "Aruba",
  "Curaçao",
  "Sint Maarten",
  "Cayman Islands",
  "British Virgin Islands",
  "US Virgin Islands",
  "Puerto Rico",
  "Guam",
  "Northern Mariana Islands",
  "American Samoa",
  "Palau",
  "Micronesia",
  "Marshall Islands",
  "Nauru",
  "Kiribati",
  "Tuvalu",
].sort();

// Major cities by country (for autocomplete suggestions)
const CITIES_BY_COUNTRY = {
  Egypt: [
    "Cairo",
    "Alexandria",
    "Giza",
    "Sharm El Sheikh",
    "Luxor",
    "Aswan",
    "Hurghada",
    "Marsa Alam",
    "Dahab",
    "Taba",
    "Port Said",
    "Suez",
    "Ismailia",
    "Mansoura",
    "Tanta",
    "Zagazig",
    "Damietta",
    "Minya",
    "Asyut",
    "Sohag",
  ],
  "United States": [
    "New York",
    "Los Angeles",
    "Chicago",
    "Houston",
    "Phoenix",
    "Philadelphia",
    "San Antonio",
    "San Diego",
    "Dallas",
    "San Jose",
    "Austin",
    "Jacksonville",
    "Fort Worth",
    "Columbus",
    "Charlotte",
    "San Francisco",
    "Indianapolis",
    "Seattle",
    "Denver",
    "Washington",
  ],
  "United Kingdom": [
    "London",
    "Manchester",
    "Birmingham",
    "Glasgow",
    "Liverpool",
    "Leeds",
    "Edinburgh",
    "Bristol",
    "Cardiff",
    "Belfast",
    "Newcastle",
    "Nottingham",
    "Sheffield",
    "Leicester",
    "Coventry",
    "Bradford",
    "Sunderland",
    "Brighton",
    "Plymouth",
    "Reading",
  ],
  Canada: [
    "Toronto",
    "Vancouver",
    "Montreal",
    "Calgary",
    "Ottawa",
    "Edmonton",
    "Winnipeg",
    "Quebec City",
    "Hamilton",
    "Kitchener",
    "London",
    "Victoria",
    "Halifax",
    "Oshawa",
    "Windsor",
    "Saskatoon",
    "Regina",
    "Sherbrooke",
    "Kelowna",
    "Barrie",
  ],
  Australia: [
    "Sydney",
    "Melbourne",
    "Brisbane",
    "Perth",
    "Adelaide",
    "Gold Coast",
    "Newcastle",
    "Canberra",
    "Sunshine Coast",
    "Wollongong",
    "Hobart",
    "Geelong",
    "Townsville",
    "Cairns",
    "Darwin",
    "Toowoomba",
    "Ballarat",
    "Bendigo",
    "Albury",
    "Launceston",
  ],
  Germany: [
    "Berlin",
    "Munich",
    "Hamburg",
    "Cologne",
    "Frankfurt",
    "Stuttgart",
    "Düsseldorf",
    "Dortmund",
    "Essen",
    "Leipzig",
    "Bremen",
    "Dresden",
    "Hannover",
    "Nuremberg",
    "Duisburg",
    "Bochum",
    "Wuppertal",
    "Bielefeld",
    "Bonn",
    "Münster",
  ],
  France: [
    "Paris",
    "Marseille",
    "Lyon",
    "Toulouse",
    "Nice",
    "Nantes",
    "Strasbourg",
    "Montpellier",
    "Bordeaux",
    "Lille",
    "Rennes",
    "Reims",
    "Le Havre",
    "Saint-Étienne",
    "Toulon",
    "Grenoble",
    "Dijon",
    "Angers",
    "Nîmes",
    "Villeurbanne",
  ],
  Italy: [
    "Rome",
    "Milan",
    "Naples",
    "Turin",
    "Palermo",
    "Genoa",
    "Bologna",
    "Florence",
    "Bari",
    "Catania",
    "Venice",
    "Verona",
    "Messina",
    "Padua",
    "Trieste",
    "Brescia",
    "Parma",
    "Taranto",
    "Prato",
    "Modena",
  ],
  Spain: [
    "Madrid",
    "Barcelona",
    "Valencia",
    "Seville",
    "Zaragoza",
    "Málaga",
    "Murcia",
    "Palma",
    "Las Palmas",
    "Bilbao",
    "Alicante",
    "Córdoba",
    "Valladolid",
    "Vigo",
    "Gijón",
    "Hospitalet",
    "Granada",
    "Vitoria",
    "A Coruña",
    "Elche",
  ],
  "United Arab Emirates": [
    "Dubai",
    "Abu Dhabi",
    "Sharjah",
    "Al Ain",
    "Ajman",
    "Ras Al Khaimah",
    "Fujairah",
    "Umm Al Quwain",
    "Khor Fakkan",
    "Dibba",
  ],
  "Saudi Arabia": [
    "Riyadh",
    "Jeddah",
    "Mecca",
    "Medina",
    "Dammam",
    "Khobar",
    "Taif",
    "Abha",
    "Tabuk",
    "Buraydah",
  ],
  Qatar: [
    "Doha",
    "Al Rayyan",
    "Al Wakrah",
    "Al Khor",
    "Dukhan",
    "Lusail",
    "Mesaieed",
    "Al Shamal",
    "Al Daayen",
    "Umm Salal",
  ],
  Jordan: [
    "Amman",
    "Zarqa",
    "Irbid",
    "Aqaba",
    "Madaba",
    "Salt",
    "Mafraq",
    "Karak",
    "Tafilah",
    "Ma'an",
  ],
  Lebanon: [
    "Beirut",
    "Tripoli",
    "Sidon",
    "Tyre",
    "Zahle",
    "Jounieh",
    "Byblos",
    "Baalbek",
    "Nabatieh",
    "Batroun",
  ],
  Morocco: [
    "Casablanca",
    "Rabat",
    "Fes",
    "Marrakech",
    "Tangier",
    "Agadir",
    "Meknes",
    "Oujda",
    "Kenitra",
    "Tetouan",
  ],
  Tunisia: [
    "Tunis",
    "Sfax",
    "Sousse",
    "Kairouan",
    "Bizerte",
    "Gabès",
    "Ariana",
    "Gafsa",
    "Monastir",
    "Ben Arous",
  ],
  Algeria: [
    "Algiers",
    "Oran",
    "Constantine",
    "Annaba",
    "Blida",
    "Batna",
    "Djelfa",
    "Sétif",
    "Sidi Bel Abbès",
    "Biskra",
  ],
  "South Africa": [
    "Cape Town",
    "Johannesburg",
    "Durban",
    "Pretoria",
    "Port Elizabeth",
    "Bloemfontein",
    "East London",
    "Polokwane",
    "Nelspruit",
    "Kimberley",
  ],
  India: [
    "Mumbai",
    "Delhi",
    "Bangalore",
    "Hyderabad",
    "Chennai",
    "Kolkata",
    "Pune",
    "Ahmedabad",
    "Jaipur",
    "Surat",
  ],
  China: [
    "Beijing",
    "Shanghai",
    "Guangzhou",
    "Shenzhen",
    "Chengdu",
    "Hangzhou",
    "Wuhan",
    "Xi'an",
    "Nanjing",
    "Tianjin",
  ],
  Japan: [
    "Tokyo",
    "Yokohama",
    "Osaka",
    "Nagoya",
    "Sapporo",
    "Fukuoka",
    "Kobe",
    "Kyoto",
    "Kawasaki",
    "Saitama",
  ],
  "South Korea": [
    "Seoul",
    "Busan",
    "Incheon",
    "Daegu",
    "Daejeon",
    "Gwangju",
    "Ulsan",
    "Suwon",
    "Changwon",
    "Goyang",
  ],
  Singapore: ["Singapore"],
  Malaysia: [
    "Kuala Lumpur",
    "George Town",
    "Ipoh",
    "Shah Alam",
    "Petaling Jaya",
    "Johor Bahru",
    "Malacca",
    "Kota Kinabalu",
    "Kuching",
    "Seremban",
  ],
  Thailand: [
    "Bangkok",
    "Chiang Mai",
    "Pattaya",
    "Phuket",
    "Hat Yai",
    "Nakhon Ratchasima",
    "Udon Thani",
    "Khon Kaen",
    "Surat Thani",
    "Nakhon Si Thammarat",
  ],
  Indonesia: [
    "Jakarta",
    "Surabaya",
    "Bandung",
    "Medan",
    "Semarang",
    "Palembang",
    "Makassar",
    "Batam",
    "Pekanbaru",
    "Denpasar",
  ],
  Philippines: [
    "Manila",
    "Quezon City",
    "Caloocan",
    "Davao",
    "Cebu",
    "Zamboanga",
    "Antipolo",
    "Pasig",
    "Taguig",
    "Valenzuela",
  ],
  Brazil: [
    "São Paulo",
    "Rio de Janeiro",
    "Brasília",
    "Salvador",
    "Fortaleza",
    "Belo Horizonte",
    "Manaus",
    "Curitiba",
    "Recife",
    "Porto Alegre",
  ],
  Argentina: [
    "Buenos Aires",
    "Córdoba",
    "Rosario",
    "Mendoza",
    "Tucumán",
    "La Plata",
    "Mar del Plata",
    "Salta",
    "Santa Fe",
    "San Juan",
  ],
  Mexico: [
    "Mexico City",
    "Guadalajara",
    "Monterrey",
    "Puebla",
    "Tijuana",
    "León",
    "Juárez",
    "Torreón",
    "Querétaro",
    "San Luis Potosí",
  ],
  Turkey: [
    "Istanbul",
    "Ankara",
    "Izmir",
    "Bursa",
    "Antalya",
    "Adana",
    "Gaziantep",
    "Konya",
    "Kayseri",
    "Mersin",
  ],
  Greece: [
    "Athens",
    "Thessaloniki",
    "Patras",
    "Heraklion",
    "Larissa",
    "Volos",
    "Ioannina",
    "Kavala",
    "Rhodes",
    "Chania",
  ],
  Portugal: [
    "Lisbon",
    "Porto",
    "Amadora",
    "Braga",
    "Setúbal",
    "Coimbra",
    "Queluz",
    "Funchal",
    "Cacém",
    "Vila Nova de Gaia",
  ],
  Netherlands: [
    "Amsterdam",
    "Rotterdam",
    "The Hague",
    "Utrecht",
    "Eindhoven",
    "Groningen",
    "Tilburg",
    "Almere",
    "Breda",
    "Nijmegen",
  ],
  Belgium: [
    "Brussels",
    "Antwerp",
    "Ghent",
    "Charleroi",
    "Liège",
    "Bruges",
    "Namur",
    "Leuven",
    "Mons",
    "Aalst",
  ],
  Switzerland: [
    "Zurich",
    "Geneva",
    "Basel",
    "Bern",
    "Lausanne",
    "Winterthur",
    "Lucerne",
    "St. Gallen",
    "Lugano",
    "Biel",
  ],
  Austria: [
    "Vienna",
    "Graz",
    "Linz",
    "Salzburg",
    "Innsbruck",
    "Klagenfurt",
    "Villach",
    "Wels",
    "Sankt Pölten",
    "Dornbirn",
  ],
  Sweden: [
    "Stockholm",
    "Gothenburg",
    "Malmö",
    "Uppsala",
    "Västerås",
    "Örebro",
    "Linköping",
    "Helsingborg",
    "Jönköping",
    "Norrköping",
  ],
  Norway: [
    "Oslo",
    "Bergen",
    "Trondheim",
    "Stavanger",
    "Bærum",
    "Kristiansand",
    "Fredrikstad",
    "Sandnes",
    "Tromsø",
    "Sarpsborg",
  ],
  Denmark: [
    "Copenhagen",
    "Aarhus",
    "Odense",
    "Aalborg",
    "Esbjerg",
    "Randers",
    "Kolding",
    "Horsens",
    "Vejle",
    "Roskilde",
  ],
  Finland: [
    "Helsinki",
    "Espoo",
    "Tampere",
    "Vantaa",
    "Oulu",
    "Turku",
    "Jyväskylä",
    "Lahti",
    "Kuopio",
    "Pori",
  ],
  Poland: [
    "Warsaw",
    "Kraków",
    "Łódź",
    "Wrocław",
    "Poznań",
    "Gdańsk",
    "Szczecin",
    "Bydgoszcz",
    "Lublin",
    "Katowice",
  ],
  Russia: [
    "Moscow",
    "Saint Petersburg",
    "Novosibirsk",
    "Yekaterinburg",
    "Kazan",
    "Nizhny Novgorod",
    "Chelyabinsk",
    "Samara",
    "Omsk",
    "Rostov-on-Don",
  ],
  Israel: [
    "Jerusalem",
    "Tel Aviv",
    "Haifa",
    "Rishon LeZion",
    "Petah Tikva",
    "Ashdod",
    "Netanya",
    "Beer Sheva",
    "Holon",
    "Bnei Brak",
  ],
};

// Get all cities as a flat list for autocomplete
const getAllCities = () => {
  const cities = [];
  Object.values(CITIES_BY_COUNTRY).forEach((countryCities) => {
    cities.push(...countryCities);
  });
  return [...new Set(cities)].sort(); // Remove duplicates and sort
};

// Translation mappings for countries (EN -> AR)
const COUNTRY_TRANSLATIONS = {
  Egypt: "مصر",
  "United States": "الولايات المتحدة",
  "United Kingdom": "المملكة المتحدة",
  Canada: "كندا",
  Australia: "أستراليا",
  Germany: "ألمانيا",
  France: "فرنسا",
  Italy: "إيطاليا",
  Spain: "إسبانيا",
  Netherlands: "هولندا",
  Belgium: "بلجيكا",
  Switzerland: "سويسرا",
  Austria: "النمسا",
  Sweden: "السويد",
  Norway: "النرويج",
  Denmark: "الدنمارك",
  Finland: "فنلندا",
  Poland: "بولندا",
  Portugal: "البرتغال",
  Greece: "اليونان",
  Turkey: "تركيا",
  "United Arab Emirates": "الإمارات العربية المتحدة",
  "Saudi Arabia": "المملكة العربية السعودية",
  Qatar: "قطر",
  Kuwait: "الكويت",
  Bahrain: "البحرين",
  Oman: "عمان",
  Jordan: "الأردن",
  Lebanon: "لبنان",
  Morocco: "المغرب",
  Tunisia: "تونس",
  Algeria: "الجزائر",
  "South Africa": "جنوب أفريقيا",
  Kenya: "كينيا",
  Nigeria: "نيجيريا",
  India: "الهند",
  China: "الصين",
  Japan: "اليابان",
  "South Korea": "كوريا الجنوبية",
  Singapore: "سنغافورة",
  Malaysia: "ماليزيا",
  Thailand: "تايلاند",
  Indonesia: "إندونيسيا",
  Philippines: "الفلبين",
  Vietnam: "فيتنام",
  Brazil: "البرازيل",
  Argentina: "الأرجنتين",
  Mexico: "المكسيك",
  Chile: "تشيلي",
  Colombia: "كولومبيا",
  Peru: "بيرو",
  "New Zealand": "نيوزيلندا",
  Ireland: "أيرلندا",
  Iceland: "آيسلندا",
  Luxembourg: "لوكسمبورغ",
  "Czech Republic": "جمهورية التشيك",
  Hungary: "المجر",
  Romania: "رومانيا",
  Bulgaria: "بلغاريا",
  Croatia: "كرواتيا",
  Serbia: "صربيا",
  Slovenia: "سلوفينيا",
  Slovakia: "سلوفاكيا",
  Estonia: "إستونيا",
  Latvia: "لاتفيا",
  Lithuania: "ليتوانيا",
  Cyprus: "قبرص",
  Malta: "مالطا",
  Israel: "إسرائيل",
  Russia: "روسيا",
  Ukraine: "أوكرانيا",
  Belarus: "بيلاروس",
  Kazakhstan: "كازاخستان",
  Uzbekistan: "أوزبكستان",
  Azerbaijan: "أذربيجان",
  Georgia: "جورجيا",
  Armenia: "أرمينيا",
  Moldova: "مولدوفا",
  Albania: "ألبانيا",
  "Bosnia and Herzegovina": "البوسنة والهرسك",
  Macedonia: "مقدونيا",
  Montenegro: "الجبل الأسود",
  Kosovo: "كوسوفو",
  Iraq: "العراق",
  Iran: "إيران",
  Pakistan: "باكستان",
  Bangladesh: "بنغلاديش",
  "Sri Lanka": "سريلانكا",
  Nepal: "نيبال",
  Bhutan: "بوتان",
  Myanmar: "ميانمار",
  Cambodia: "كمبوديا",
  Laos: "لاوس",
  Mongolia: "منغوليا",
  Taiwan: "تايوان",
  "Hong Kong": "هونغ كونغ",
  Macau: "ماكاو",
};

// Translation mappings for cities (EN -> AR) - Focus on Egypt and major cities
const CITY_TRANSLATIONS = {
  // Egypt
  Cairo: "القاهرة",
  Alexandria: "الإسكندرية",
  Giza: "الجيزة",
  "Sharm El Sheikh": "شرم الشيخ",
  Luxor: "الأقصر",
  Aswan: "أسوان",
  Hurghada: "الغردقة",
  "Marsa Alam": "مرسى علم",
  Dahab: "دهب",
  Taba: "طابا",
  "Port Said": "بورسعيد",
  Suez: "السويس",
  Ismailia: "الإسماعيلية",
  Mansoura: "المنصورة",
  Tanta: "طنطا",
  Zagazig: "الزقازيق",
  Damietta: "دمياط",
  Minya: "المنيا",
  Asyut: "أسيوط",
  Sohag: "سوهاج",
  // UAE
  Dubai: "دبي",
  "Abu Dhabi": "أبو ظبي",
  Sharjah: "الشارقة",
  "Al Ain": "العين",
  Ajman: "عجمان",
  "Ras Al Khaimah": "رأس الخيمة",
  Fujairah: "الفجيرة",
  "Umm Al Quwain": "أم القيوين",
  // Saudi Arabia
  Riyadh: "الرياض",
  Jeddah: "جدة",
  Mecca: "مكة المكرمة",
  Medina: "المدينة المنورة",
  Dammam: "الدمام",
  Khobar: "الخبر",
  Taif: "الطائف",
  Abha: "أبها",
  Tabuk: "تبوك",
  Buraydah: "بريدة",
  // Qatar
  Doha: "الدوحة",
  "Al Rayyan": "الريان",
  "Al Wakrah": "الوكرة",
  "Al Khor": "الخور",
  // Jordan
  Amman: "عمان",
  Zarqa: "الزرقاء",
  Irbid: "إربد",
  Aqaba: "العقبة",
  Madaba: "مادبا",
  Salt: "السلط",
  Mafraq: "المفرق",
  Karak: "الكرك",
  Tafilah: "الطفيلة",
  "Ma'an": "معان",
  // Lebanon
  Beirut: "بيروت",
  Tripoli: "طرابلس",
  Sidon: "صيدا",
  Tyre: "صور",
  Zahle: "زحلة",
  Jounieh: "جونيه",
  Byblos: "جبيل",
  Baalbek: "بعلبك",
  Nabatieh: "النبطية",
  Batroun: "البترون",
  // Morocco
  Casablanca: "الدار البيضاء",
  Rabat: "الرباط",
  Fes: "فاس",
  Marrakech: "مراكش",
  Tangier: "طنجة",
  Agadir: "أغادير",
  Meknes: "مكناس",
  Oujda: "وجدة",
  Kenitra: "القنيطرة",
  Tetouan: "تطوان",
  // Tunisia
  Tunis: "تونس",
  Sfax: "صفاقس",
  Sousse: "سوسة",
  Kairouan: "القيروان",
  Bizerte: "بنزرت",
  Gabès: "قابس",
  Ariana: "أريانة",
  Gafsa: "قفصة",
  Monastir: "المنستير",
  "Ben Arous": "بن عروس",
  // Algeria
  Algiers: "الجزائر",
  Oran: "وهران",
  Constantine: "قسنطينة",
  Annaba: "عنابة",
  Blida: "البليدة",
  Batna: "باتنة",
  Djelfa: "الجلفة",
  Sétif: "سطيف",
  "Sidi Bel Abbès": "سيدي بلعباس",
  Biskra: "بسكرة",
  // Other major cities
  London: "لندن",
  Paris: "باريس",
  "New York": "نيويورك",
  "Los Angeles": "لوس أنجلوس",
  Tokyo: "طوكيو",
  Moscow: "موسكو",
  Istanbul: "إسطنبول",
  Athens: "أثينا",
  Rome: "روما",
  Madrid: "مدريد",
  Barcelona: "برشلونة",
  Berlin: "برلين",
  Munich: "ميونخ",
  Vienna: "فيينا",
  Amsterdam: "أمستردام",
  Brussels: "بروكسل",
  Stockholm: "ستوكهولم",
  Oslo: "أوسلو",
  Copenhagen: "كوبنهاغن",
  Helsinki: "هلسنكي",
  Warsaw: "وارسو",
  Prague: "براغ",
  Budapest: "بودابست",
  Bucharest: "بوخارست",
  Sofia: "صوفيا",
  Zagreb: "زغرب",
  Belgrade: "بلغراد",
  Mumbai: "مومباي",
  Delhi: "دلهي",
  Bangalore: "بنغالور",
  Hyderabad: "حيدر أباد",
  Chennai: "تشيناي",
  Kolkata: "كولكاتا",
  Beijing: "بكين",
  Shanghai: "شنغهاي",
  Guangzhou: "قوانغتشو",
  Shenzhen: "شنتشن",
  Seoul: "سيول",
  Busan: "بوسان",
  Singapore: "سنغافورة",
  "Kuala Lumpur": "كوالا لومبور",
  Bangkok: "بانكوك",
  Jakarta: "جاكرتا",
  Manila: "مانيلا",
  Sydney: "سيدني",
  Melbourne: "ملبورن",
  Toronto: "تورونتو",
  Vancouver: "فانكوفر",
  Montreal: "مونتريال",
  "São Paulo": "ساو باولو",
  "Rio de Janeiro": "ريو دي جانيرو",
  "Buenos Aires": "بوينس آيرس",
  "Mexico City": "مدينة مكسيكو",
  Jerusalem: "القدس",
  "Tel Aviv": "تل أبيب",
  Haifa: "حيفا",
};

// Helper function to get Arabic translation
const getArabicTranslation = (englishText, translationMap) => {
  return translationMap[englishText] || englishText;
};

// Step labels
const steps = [
  "Basic Information",
  "Location & Address",
  "Property Details",
  "Compound Information",
  "WiFi & Utilities",
  "Amenities & Criteria",
  "Media Upload",
  "Legal Papers",
];

function AddProperty() {
  const navigate = useNavigate();
  const [showInfoModal, setShowInfoModal] = useState(true);
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [errors, setErrors] = useState({});
  const [activeStep, setActiveStep] = useState(0);
  const [amenitiesList, setAmenitiesList] = useState([]);
  const [criteriaList, setCriteriaList] = useState([]);
  const [loadingData, setLoadingData] = useState(true);

  // Form state
  const [formData, setFormData] = useState({
    titleEn: "",
    titleAr: "",
    descriptionEn: "",
    descriptionAr: "",
    propertyType: "",
    rooms: "",
    bathrooms: "",
    balconies: "",
    size: "",
    floors: "",
    floorNumber: "",
    addressEn: "",
    addressAr: "",
    cityEn: "",
    cityAr: "",
    countryEn: "",
    countryAr: "",
    googleMapLink: "",
    landmark: "",
    landmarkAr: "",
    wifiName: "",
    wifiPassword: "",
    buildingAge: "",
    furnitureAge: "",
    apartmentsPerFloor: "",
    isInCompound: false,
    compoundName: "",
    compoundNameAr: "",
    compoundFacilities: [],
    electricityMeterType: "",
    waterMeterType: "",
    propertyView: "",
    accessCode: "",
    currentPropertyState: "Available",
    readyForInspector: "",
    currentRentalProcess: "",
    currentRentalRevenue: "",
    preferredInspectionTime: "",
    amenities: [],
    criteria: [],
  });

  // File state with previews
  const [images, setImages] = useState([]);
  const [imagePreviews, setImagePreviews] = useState([]);
  const [videos, setVideos] = useState([]);
  const [videoPreviews, setVideoPreviews] = useState([]);
  const [documents, setDocuments] = useState([]);
  const [buildingImages, setBuildingImages] = useState([]);
  const [buildingImagePreviews, setBuildingImagePreviews] = useState([]);
  const [legalPapers, setLegalPapers] = useState([]);

  // Legal documents (specific types)
  const [propertyOwnerId, setPropertyOwnerId] = useState(null);
  const [propertyOwnerIdPreview, setPropertyOwnerIdPreview] = useState(null);
  const [rentalOrOwnershipContract, setRentalOrOwnershipContract] = useState(null);
  const [rentalOrOwnershipContractPreview, setRentalOrOwnershipContractPreview] = useState(null);
  const [powerOfAttorney, setPowerOfAttorney] = useState(null);
  const [powerOfAttorneyPreview, setPowerOfAttorneyPreview] = useState(null);

  // Room and bathroom photos
  const [roomPhotos, setRoomPhotos] = useState({}); // { room1: File, room2: File, ... }
  const [roomPhotoPreviews, setRoomPhotoPreviews] = useState({});
  const [bathroomPhotos, setBathroomPhotos] = useState({}); // { bathroom1: File, bathroom2: File, ... }
  const [bathroomPhotoPreviews, setBathroomPhotoPreviews] = useState({});
  const [balconyPhotos, setBalconyPhotos] = useState({}); // { balcony1: File, balcony2: File, ... }
  const [balconyPhotoPreviews, setBalconyPhotoPreviews] = useState({});
  const [livingRoomPhoto, setLivingRoomPhoto] = useState(null);
  const [livingRoomPhotoPreview, setLivingRoomPhotoPreview] = useState(null);

  // Fetch amenities and criteria on component mount
  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoadingData(true);
        const [amenitiesRes, criteriaRes] = await Promise.all([
          propertyOwnerAPI.getAmenities(),
          propertyOwnerAPI.getCriteria(),
        ]);
        setAmenitiesList(amenitiesRes.data || []);
        setCriteriaList(criteriaRes.data || []);
      } catch (error) {
        console.error("Error fetching amenities/criteria:", error);
        // Show error message but don't use static data
        setAmenitiesList([]);
        setCriteriaList([]);
      } finally {
        setLoadingData(false);
      }
    };
    fetchData();
  }, []);

  // Handle input change
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: "" }));
    }
  };

  // Handle image upload with preview
  const handleImageChange = (e) => {
    const files = Array.from(e.target.files);
    if (files.length + images.length > 3) {
      setErrors((prev) => ({
        ...prev,
        images: "Maximum 3 property images allowed",
      }));
      return;
    }

    const newImages = [...images, ...files];
    setImages(newImages);

    // Create previews
    const newPreviews = [];
    files.forEach((file) => {
      const reader = new FileReader();
      reader.onload = (e) => {
        newPreviews.push({ file: file.name, url: e.target.result });
        if (newPreviews.length === files.length) {
          setImagePreviews((prev) => [...prev, ...newPreviews]);
        }
      };
      reader.readAsDataURL(file);
    });

    setErrors((prev) => ({ ...prev, images: "" }));
  };

  // Remove image
  const removeImage = (index) => {
    const newImages = [...images];
    const newPreviews = [...imagePreviews];
    newImages.splice(index, 1);
    newPreviews.splice(index, 1);
    setImages(newImages);
    setImagePreviews(newPreviews);
  };

  // Handle building image upload with preview
  const handleBuildingImageChange = (e) => {
    const files = Array.from(e.target.files);
    if (files.length + buildingImages.length > 1) {
      setErrors((prev) => ({
        ...prev,
        buildingImages: "Maximum 1 building image allowed",
      }));
      return;
    }

    const newImages = [...buildingImages, ...files];
    setBuildingImages(newImages);

    // Create previews
    const newPreviews = [];
    files.forEach((file) => {
      const reader = new FileReader();
      reader.onload = (e) => {
        newPreviews.push({ file: file.name, url: e.target.result });
        if (newPreviews.length === files.length) {
          setBuildingImagePreviews((prev) => [...prev, ...newPreviews]);
        }
      };
      reader.readAsDataURL(file);
    });

    setErrors((prev) => ({ ...prev, buildingImages: "" }));
  };

  // Remove building image
  const removeBuildingImage = (index) => {
    const newImages = [...buildingImages];
    const newPreviews = [...buildingImagePreviews];
    newImages.splice(index, 1);
    newPreviews.splice(index, 1);
    setBuildingImages(newImages);
    setBuildingImagePreviews(newPreviews);
  };

  // Handle compound facilities
  const handleCompoundFacilityChange = (facility) => {
    setFormData((prev) => {
      const facilities = prev.compoundFacilities || [];
      // Check if facility is already selected (compare by en name)
      const facilityEn = typeof facility === "object" ? facility.en : facility;
      const isSelected = facilities.some((f) => {
        const fEn = typeof f === "object" ? f.en : f;
        return fEn === facilityEn;
      });

      if (isSelected) {
        return {
          ...prev,
          compoundFacilities: facilities.filter((f) => {
            const fEn = typeof f === "object" ? f.en : f;
            return fEn !== facilityEn;
          }),
        };
      } else {
        // Add as multilingual object
        const facilityObj =
          typeof facility === "object" ? facility : { en: facility, ar: facility };
        return {
          ...prev,
          compoundFacilities: [...facilities, facilityObj],
        };
      }
    });
  };

  // Step navigation
  const handleNext = () => {
    // Validate current step before proceeding
    if (validateStep(activeStep)) {
      setActiveStep((prevActiveStep) => prevActiveStep + 1);
    }
  };

  const handleBack = () => {
    setActiveStep((prevActiveStep) => prevActiveStep - 1);
  };

  const handleStep = (step) => {
    // Allow navigation to completed steps (going back)
    if (step < activeStep) {
      setActiveStep(step);
      return;
    }

    // Allow navigation to the next step only if current step is valid
    if (step === activeStep + 1) {
      if (validateStep(activeStep)) {
        setActiveStep(step);
      } else {
        // Show error message if validation fails
        console.log("Please complete the current step before proceeding");
      }
      return;
    }

    // Don't allow skipping steps - user must complete current step first
    if (step > activeStep + 1) {
      // Validate current step first
      if (!validateStep(activeStep)) {
        console.log("Please complete the current step before proceeding to future steps");
        return;
      }
      // If current step is valid, allow navigation
      setActiveStep(step);
      return;
    }

    // If clicking on the same step, do nothing
    if (step === activeStep) {
      return;
    }
  };

  // Validate specific step
  const validateStep = (step) => {
    const newErrors = {};

    // Helper: validate positive integer (for rooms, bathrooms)
    const requirePositiveInt = (value, min = 1) => {
      if (value === "" || value === undefined || value === null) return false;
      const n = parseInt(String(value).trim(), 10);
      return !Number.isNaN(n) && n >= min;
    };
    // Helper: validate positive number (for size)
    const requirePositiveNumber = (value) => {
      if (value === "" || value === undefined || value === null) return false;
      const n = parseFloat(String(value).trim());
      return !Number.isNaN(n) && n > 0;
    };

    switch (step) {
      case 0: // Basic Information
        if (!formData.titleEn || !formData.titleEn.trim()) {
          newErrors.titleEn = "Property title (English) is required";
        }
        if (!formData.titleAr || !formData.titleAr.trim()) {
          newErrors.titleAr = "Property title (Arabic) is required";
        }
        if (!formData.propertyType) {
          newErrors.propertyType = "Property type is required";
        }
        break;
      case 1: // Location & Address
        if (!formData.addressEn || !formData.addressEn.trim()) {
          newErrors.addressEn = "Address (English) is required";
        }
        if (!formData.addressAr || !formData.addressAr.trim()) {
          newErrors.addressAr = "Address (Arabic) is required";
        }
        if (!formData.countryEn || !formData.countryEn.trim()) {
          newErrors.countryEn = "Country is required";
        }
        if (!formData.cityEn || !formData.cityEn.trim()) {
          newErrors.cityEn = "City is required";
        }
        if (!formData.googleMapLink || !formData.googleMapLink.trim()) {
          newErrors.googleMapLink = "Google Map Link is required";
        }
        break;
      case 2: // Property Details (rooms, bathrooms, size, floor, living room + room + bathroom photos)
        // Rooms: required, integer >= 1
        if (!requirePositiveInt(formData.rooms, 1)) {
          newErrors.rooms = "Number of rooms is required and must be a whole number (minimum 1)";
        }
        // Bathrooms: required, integer >= 1
        if (!requirePositiveInt(formData.bathrooms, 1)) {
          newErrors.bathrooms =
            "Number of bathrooms is required and must be a whole number (minimum 1)";
        }
        // Size: required, positive number
        if (!requirePositiveNumber(formData.size)) {
          newErrors.size = "Size (m²) is required and must be a positive number";
        }
        // Floor number for apartments
        if (formData.propertyType === "apartment") {
          if (!requirePositiveInt(formData.floorNumber, 1)) {
            newErrors.floorNumber =
              "Floor number is required for apartments (whole number, minimum 1)";
          }
        }
        // Optional numeric fields: if filled, must be valid numbers
        if (formData.balconies !== "" && formData.balconies != null) {
          const b = parseInt(String(formData.balconies), 10);
          if (Number.isNaN(b) || b < 0)
            newErrors.balconies = "Balconies must be 0 or a whole number";
        }
        // Total Floors: required, whole number >= 1
        if (!requirePositiveInt(formData.floors, 1)) {
          newErrors.floors = "Total floors is required and must be a whole number (minimum 1)";
        }
        // Building Age: required, number >= 0
        if (formData.buildingAge === "" || formData.buildingAge == null) {
          newErrors.buildingAge = "Building age is required";
        } else {
          const a = parseFloat(String(formData.buildingAge));
          if (Number.isNaN(a) || a < 0)
            newErrors.buildingAge = "Building age must be 0 or a valid number";
        }
        // Furniture Age: required, number >= 0
        if (formData.furnitureAge === "" || formData.furnitureAge == null) {
          newErrors.furnitureAge = "Furniture age is required";
        } else {
          const fa = parseFloat(String(formData.furnitureAge));
          if (Number.isNaN(fa) || fa < 0)
            newErrors.furnitureAge = "Furniture age must be 0 or a valid number";
        }
        // Property View: required
        if (!formData.propertyView || !formData.propertyView.trim()) {
          newErrors.propertyView = "Property view is required";
        }
        // Current Property State: required
        if (!formData.currentPropertyState || !formData.currentPropertyState.trim()) {
          newErrors.currentPropertyState = "Current property state is required";
        }
        // Ready for Inspector: required (must be explicitly true or false)
        if (formData.readyForInspector !== "true" && formData.readyForInspector !== "false") {
          newErrors.readyForInspector =
            "Please select whether the property is ready for inspector (Yes or No)";
        }
        // Current Rental Process: required
        if (!formData.currentRentalProcess || !formData.currentRentalProcess.trim()) {
          newErrors.currentRentalProcess = "Current rental process is required";
        }
        // Current Rental Revenue: required (can be 0)
        if (formData.currentRentalRevenue === "" || formData.currentRentalRevenue == null) {
          newErrors.currentRentalRevenue = "Current rental revenue is required";
        } else {
          const rev = parseFloat(String(formData.currentRentalRevenue));
          if (Number.isNaN(rev) || rev < 0)
            newErrors.currentRentalRevenue = "Current rental revenue must be 0 or a valid number";
        }
        // Preferred Inspection Time: required only when ready for inspector is Yes
        if (formData.readyForInspector === "true") {
          if (!formData.preferredInspectionTime || !formData.preferredInspectionTime.trim()) {
            newErrors.preferredInspectionTime =
              "Preferred time for inspection is required when property is ready for inspector";
          }
        }
        // Living room photo required
        if (!livingRoomPhoto) {
          newErrors.livingRoomPhoto = "Living room photo is required";
        }
        // Room photos: one per room
        const roomsCount = requirePositiveInt(formData.rooms, 1)
          ? parseInt(String(formData.rooms).trim(), 10)
          : 0;
        for (let i = 1; i <= roomsCount; i++) {
          const roomKey = `room${i}`;
          if (!roomPhotos[roomKey]) {
            newErrors.roomPhotos =
              newErrors.roomPhotos ||
              "Please upload one photo for each room (Room 1 to " + roomsCount + ")";
            break;
          }
        }
        // Bathroom photos: one per bathroom
        const bathroomsCount = requirePositiveInt(formData.bathrooms, 1)
          ? parseInt(String(formData.bathrooms).trim(), 10)
          : 0;
        for (let i = 1; i <= bathroomsCount; i++) {
          const bathroomKey = `bathroom${i}`;
          if (!bathroomPhotos[bathroomKey]) {
            newErrors.bathroomPhotos =
              newErrors.bathroomPhotos ||
              "Please upload one photo for each bathroom (Bathroom 1 to " + bathroomsCount + ")";
            break;
          }
        }
        // Balcony photos: when user entered number of balconies > 0, one photo per balcony required
        const balconiesCount = (() => {
          if (formData.balconies === "" || formData.balconies == null) return 0;
          const b = parseInt(String(formData.balconies).trim(), 10);
          return !Number.isNaN(b) && b > 0 ? b : 0;
        })();
        for (let i = 1; i <= balconiesCount; i++) {
          const balconyKey = `balcony${i}`;
          if (!balconyPhotos[balconyKey]) {
            newErrors.balconyPhotos =
              newErrors.balconyPhotos ||
              "Please upload one photo for each balcony (Balcony 1 to " + balconiesCount + ")";
            break;
          }
        }
        break;
      case 3: // Compound Information
        if (formData.isInCompound) {
          if (!formData.compoundName || !formData.compoundName.trim()) {
            newErrors.compoundName = "Compound name is required when property is inside a compound";
          }
        }
        break;
      case 4: // WiFi & Utilities
        if (!formData.electricityMeterType || !formData.electricityMeterType.trim()) {
          newErrors.electricityMeterType = "Electricity meter type is required";
        }
        if (!formData.waterMeterType || !formData.waterMeterType.trim()) {
          newErrors.waterMeterType = "Water meter type is required";
        }
        break;
      case 5: // Amenities & Criteria
        if (!formData.amenities || formData.amenities.length < 1) {
          newErrors.amenities = "Please select at least one amenity";
        }
        if (!formData.criteria || formData.criteria.length < 1) {
          newErrors.criteria = "Please select at least one criterion";
        }
        break;
      case 6: // Media Upload
        if (images.length === 0) {
          newErrors.images = "At least one property image is required";
        }
        if (videos.length < 1) {
          newErrors.videos = "Property video is required (at least one video)";
        }
        if (buildingImages.length < 1) {
          newErrors.buildingImages = "At least one building image is required";
        }
        break;
      case 7: // Legal Papers
        if (!propertyOwnerId) {
          newErrors.propertyOwnerId = "Property Owner ID document is required";
        }
        if (!rentalOrOwnershipContract) {
          newErrors.rentalOrOwnershipContract = "Rental or Ownership Contract document is required";
        }
        break;
      default:
        break;
    }

    setErrors((prev) => ({ ...prev, ...newErrors }));
    return Object.keys(newErrors).length === 0;
  };

  // Handle video upload with preview
  const handleVideoChange = (e) => {
    const files = Array.from(e.target.files);
    if (files.length + videos.length > 1) {
      setErrors((prev) => ({
        ...prev,
        videos: "Maximum 1 video allowed",
      }));
      return;
    }

    const newVideos = [...videos, ...files];
    setVideos(newVideos);

    // Create previews for videos
    const newPreviews = [];
    files.forEach((file) => {
      newPreviews.push({ file: file.name, url: URL.createObjectURL(file) });
    });
    setVideoPreviews((prev) => [...prev, ...newPreviews]);

    setErrors((prev) => ({ ...prev, videos: "" }));
  };

  // Remove video
  const removeVideo = (index) => {
    const newVideos = [...videos];
    const newPreviews = [...videoPreviews];
    URL.revokeObjectURL(newPreviews[index].url);
    newVideos.splice(index, 1);
    newPreviews.splice(index, 1);
    setVideos(newVideos);
    setVideoPreviews(newPreviews);
  };

  // Handle document upload
  const handleDocumentChange = (e) => {
    const files = Array.from(e.target.files);
    if (files.length + documents.length > 10) {
      setErrors((prev) => ({
        ...prev,
        documents: "Maximum 10 documents allowed",
      }));
      return;
    }
    setDocuments((prev) => [...prev, ...files]);
    setErrors((prev) => ({ ...prev, documents: "" }));
  };

  // Remove document
  const removeDocument = (index) => {
    const newDocuments = [...documents];
    newDocuments.splice(index, 1);
    setDocuments(newDocuments);
  };

  // Handle legal papers upload (legacy - keeping for backward compatibility)
  const handleLegalPaperChange = (e) => {
    const files = Array.from(e.target.files);
    if (files.length + legalPapers.length > 10) {
      setErrors((prev) => ({
        ...prev,
        legalPapers: "Maximum 10 legal papers allowed",
      }));
      return;
    }
    setLegalPapers((prev) => [...prev, ...files]);
    setErrors((prev) => ({ ...prev, legalPapers: "" }));
  };

  // Remove legal paper (legacy)
  const removeLegalPaper = (index) => {
    const newLegalPapers = [...legalPapers];
    newLegalPapers.splice(index, 1);
    setLegalPapers(newLegalPapers);
  };

  // Handle specific legal document uploads
  const handlePropertyOwnerIdChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.type !== "application/pdf") {
        setErrors((prev) => ({
          ...prev,
          propertyOwnerId: "Please upload a PDF file",
        }));
        return;
      }
      setPropertyOwnerId(file);
      setErrors((prev) => ({ ...prev, propertyOwnerId: "" }));
    }
  };

  const handleRentalOrOwnershipContractChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.type !== "application/pdf") {
        setErrors((prev) => ({
          ...prev,
          rentalOrOwnershipContract: "Please upload a PDF file",
        }));
        return;
      }
      setRentalOrOwnershipContract(file);
      setErrors((prev) => ({ ...prev, rentalOrOwnershipContract: "" }));
    }
  };

  const handlePowerOfAttorneyChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.type !== "application/pdf") {
        setErrors((prev) => ({
          ...prev,
          powerOfAttorney: "Please upload a PDF file",
        }));
        return;
      }
      setPowerOfAttorney(file);
      setErrors((prev) => ({ ...prev, powerOfAttorney: "" }));
    }
  };

  // Handle amenities toggle
  const handleAmenityToggle = (amenity) => {
    setFormData((prev) => {
      // Handle both API format (with _id, name.en, name.ar) and legacy format (with en, ar)
      const amenityEn = amenity.name?.en || amenity.en || amenity;
      const amenityAr = amenity.name?.ar || amenity.ar || amenityEn;

      const isSelected = prev.amenities.some((a) => {
        const aEn = typeof a === "object" ? a.en || a.name?.en : a;
        return aEn === amenityEn;
      });

      if (isSelected) {
        return {
          ...prev,
          amenities: prev.amenities.filter((a) => {
            const aEn = typeof a === "object" ? a.en || a.name?.en : a;
            return aEn !== amenityEn;
          }),
        };
      } else {
        // Add as multilingual object
        const amenityObj = { en: amenityEn, ar: amenityAr };
        return {
          ...prev,
          amenities: [...prev.amenities, amenityObj],
        };
      }
    });
  };

  // Handle criteria toggle
  const handleCriteriaToggle = (criterion) => {
    setFormData((prev) => {
      const criterionNameEn = criterion.name?.en || "";
      const criterionNameAr = criterion.name?.ar || criterionNameEn;
      const criterionCategoryEn = criterion.category?.en || "General";
      const criterionCategoryAr = criterion.category?.ar || criterionCategoryEn;
      const isQuantifiable = criterion.quantifiable === true;

      const isSelected = prev.criteria.some((c) => {
        const cName = typeof c.name === "object" ? c.name.en || c.name : c.name || c;
        return cName === criterionNameEn;
      });

      if (isSelected) {
        return {
          ...prev,
          criteria: prev.criteria.filter((c) => {
            const cName = typeof c.name === "object" ? c.name.en || c.name : c.name || c;
            return cName !== criterionNameEn;
          }),
        };
      } else {
        return {
          ...prev,
          criteria: [
            ...prev.criteria,
            {
              name: { en: criterionNameEn, ar: criterionNameAr },
              category: { en: criterionCategoryEn, ar: criterionCategoryAr },
              quantifiable: isQuantifiable,
              quantity: isQuantifiable ? 1 : undefined,
            },
          ],
        };
      }
    });
  };

  // Handle criteria quantity change (for quantifiable items)
  const handleCriteriaQuantityChange = (criterionNameEn, newQuantity) => {
    const qty = Math.max(1, parseInt(newQuantity, 10) || 1);
    setFormData((prev) => ({
      ...prev,
      criteria: prev.criteria.map((c) => {
        const cName = typeof c.name === "object" ? c.name.en || c.name : c.name || c;
        if (cName === criterionNameEn) {
          return { ...c, quantity: qty };
        }
        return c;
      }),
    }));
  };

  // Validate form (full form validation on final submit)
  const validateForm = () => {
    const newErrors = {};

    // Required field validations - multilingual
    if (!formData.titleEn || !formData.titleEn.trim()) {
      newErrors.titleEn = "Property title (English) is required";
    }
    if (!formData.titleAr || !formData.titleAr.trim()) {
      newErrors.titleAr = "Property title (Arabic) is required";
    }
    if (!formData.propertyType) {
      newErrors.propertyType = "Property type is required";
    }

    // Rooms: required, integer >= 1
    const roomsNum = parseInt(String(formData.rooms).trim(), 10);
    if (!formData.rooms || formData.rooms === "" || Number.isNaN(roomsNum) || roomsNum < 1) {
      newErrors.rooms = "Number of rooms is required and must be a whole number (minimum 1)";
    }

    // Bathrooms: required, integer >= 1
    const bathroomsNum = parseInt(String(formData.bathrooms).trim(), 10);
    if (
      !formData.bathrooms ||
      formData.bathrooms === "" ||
      Number.isNaN(bathroomsNum) ||
      bathroomsNum < 1
    ) {
      newErrors.bathrooms =
        "Number of bathrooms is required and must be a whole number (minimum 1)";
    }

    // Size: required, positive number
    const sizeNum = parseFloat(String(formData.size).trim());
    if (!formData.size || formData.size === "" || Number.isNaN(sizeNum) || sizeNum <= 0) {
      newErrors.size = "Size (m²) is required and must be a positive number";
    }

    if (!formData.addressEn || !formData.addressEn.trim()) {
      newErrors.addressEn = "Address (English) is required";
    }
    if (!formData.addressAr || !formData.addressAr.trim()) {
      newErrors.addressAr = "Address (Arabic) is required";
    }
    if (!formData.countryEn || !formData.countryEn.trim()) {
      newErrors.countryEn = "Country is required";
    }
    if (!formData.cityEn || !formData.cityEn.trim()) {
      newErrors.cityEn = "City is required";
    }
    if (!formData.googleMapLink || !formData.googleMapLink.trim()) {
      newErrors.googleMapLink = "Google Map Link is required";
    }

    // Total Floors, Building Age, Furniture Age, Property View, State, Ready for Inspector, Rental Process, Revenue
    const floorsNum = parseInt(String(formData.floors).trim(), 10);
    if (!formData.floors || formData.floors === "" || Number.isNaN(floorsNum) || floorsNum < 1) {
      newErrors.floors = "Total floors is required and must be a whole number (minimum 1)";
    }
    if (formData.buildingAge === "" || formData.buildingAge == null) {
      newErrors.buildingAge = "Building age is required";
    } else {
      const a = parseFloat(String(formData.buildingAge));
      if (Number.isNaN(a) || a < 0)
        newErrors.buildingAge = "Building age must be 0 or a valid number";
    }
    if (formData.furnitureAge === "" || formData.furnitureAge == null) {
      newErrors.furnitureAge = "Furniture age is required";
    } else {
      const fa = parseFloat(String(formData.furnitureAge));
      if (Number.isNaN(fa) || fa < 0)
        newErrors.furnitureAge = "Furniture age must be 0 or a valid number";
    }
    if (!formData.propertyView || !formData.propertyView.trim()) {
      newErrors.propertyView = "Property view is required";
    }
    if (!formData.currentPropertyState || !formData.currentPropertyState.trim()) {
      newErrors.currentPropertyState = "Current property state is required";
    }
    if (formData.readyForInspector !== "true" && formData.readyForInspector !== "false") {
      newErrors.readyForInspector =
        "Please select whether the property is ready for inspector (Yes or No)";
    }
    if (!formData.currentRentalProcess || !formData.currentRentalProcess.trim()) {
      newErrors.currentRentalProcess = "Current rental process is required";
    }
    if (formData.currentRentalRevenue === "" || formData.currentRentalRevenue == null) {
      newErrors.currentRentalRevenue = "Current rental revenue is required";
    } else {
      const rev = parseFloat(String(formData.currentRentalRevenue));
      if (Number.isNaN(rev) || rev < 0)
        newErrors.currentRentalRevenue = "Current rental revenue must be 0 or a valid number";
    }
    if (
      formData.readyForInspector === "true" &&
      (!formData.preferredInspectionTime || !formData.preferredInspectionTime.trim())
    ) {
      newErrors.preferredInspectionTime =
        "Preferred time for inspection is required when property is ready for inspector";
    }
    if (formData.isInCompound && (!formData.compoundName || !formData.compoundName.trim())) {
      newErrors.compoundName = "Compound name is required when property is inside a compound";
    }
    if (!formData.electricityMeterType || !formData.electricityMeterType.trim()) {
      newErrors.electricityMeterType = "Electricity meter type is required";
    }
    if (!formData.waterMeterType || !formData.waterMeterType.trim()) {
      newErrors.waterMeterType = "Water meter type is required";
    }
    if (!formData.amenities || formData.amenities.length < 1) {
      newErrors.amenities = "Please select at least one amenity";
    }
    if (!formData.criteria || formData.criteria.length < 1) {
      newErrors.criteria = "Please select at least one criterion";
    }

    // Apartment-specific validation
    if (formData.propertyType === "apartment") {
      const floorNum = parseInt(String(formData.floorNumber).trim(), 10);
      if (
        !formData.floorNumber ||
        formData.floorNumber === "" ||
        Number.isNaN(floorNum) ||
        floorNum < 1
      ) {
        newErrors.floorNumber = "Floor number is required for apartments (whole number, minimum 1)";
      }
    }

    // File validations
    if (images.length === 0) {
      newErrors.images = "At least one property image is required";
    }
    if (videos.length < 1) {
      newErrors.videos = "Property video is required (at least one video)";
    }
    if (buildingImages.length < 1) {
      newErrors.buildingImages = "At least one building image is required";
    }
    if (!livingRoomPhoto) {
      newErrors.livingRoomPhoto = "Living room photo is required";
    }

    // Room photos: one per room
    const roomsCount = !Number.isNaN(roomsNum) && roomsNum >= 1 ? roomsNum : 0;
    for (let i = 1; i <= roomsCount; i++) {
      if (!roomPhotos[`room${i}`]) {
        newErrors.roomPhotos = "Please upload one photo for each room";
        break;
      }
    }
    // Bathroom photos: one per bathroom
    const bathroomsCount = !Number.isNaN(bathroomsNum) && bathroomsNum >= 1 ? bathroomsNum : 0;
    for (let i = 1; i <= bathroomsCount; i++) {
      if (!bathroomPhotos[`bathroom${i}`]) {
        newErrors.bathroomPhotos = "Please upload one photo for each bathroom";
        break;
      }
    }

    // Balcony photos: when user entered number of balconies > 0, one photo per balcony required
    const balconiesNum = parseInt(String(formData.balconies).trim(), 10);
    const balconiesCount = !Number.isNaN(balconiesNum) && balconiesNum > 0 ? balconiesNum : 0;
    for (let i = 1; i <= balconiesCount; i++) {
      if (!balconyPhotos[`balcony${i}`]) {
        newErrors.balconyPhotos = "Please upload one photo for each balcony";
        break;
      }
    }

    // Legal documents validation
    if (!propertyOwnerId) {
      newErrors.propertyOwnerId = "Property Owner ID is required";
    }
    if (!rentalOrOwnershipContract) {
      newErrors.rentalOrOwnershipContract = "Rental Contract or Ownership Contract is required";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setLoading(true);
    setSuccess(false);

    try {
      // Create FormData
      const submitFormData = new FormData();

      // Add form fields (clear conditional fields when not applicable)
      Object.keys(formData).forEach((key) => {
        if (key === "amenities") {
          submitFormData.append("amenities", JSON.stringify(formData.amenities));
        } else if (key === "criteria") {
          submitFormData.append("criteria", JSON.stringify(formData.criteria));
        } else if (key === "preferredInspectionTime" && formData.readyForInspector !== "true") {
          submitFormData.append(key, "");
        } else if ((key === "compoundName" || key === "compoundNameAr") && !formData.isInCompound) {
          submitFormData.append(key, "");
        } else {
          submitFormData.append(key, formData[key]);
        }
      });

      // Add files
      images.forEach((file) => {
        submitFormData.append("images", file);
      });
      videos.forEach((file) => {
        submitFormData.append("videos", file);
      });
      documents.forEach((file) => {
        submitFormData.append("officialDocuments", file);
      });
      buildingImages.forEach((file) => {
        submitFormData.append("buildingImages", file);
      });
      legalPapers.forEach((file) => {
        submitFormData.append("legalPapers", file);
      });

      // Add specific legal documents
      if (propertyOwnerId) {
        submitFormData.append("propertyOwnerId", propertyOwnerId);
      }
      if (rentalOrOwnershipContract) {
        submitFormData.append("rentalOrOwnershipContract", rentalOrOwnershipContract);
      }
      if (powerOfAttorney) {
        submitFormData.append("powerOfAttorney", powerOfAttorney);
      }

      // Add living room photo
      if (livingRoomPhoto) {
        submitFormData.append("livingRoomPhoto", livingRoomPhoto);
      }

      // Add room photos with their keys
      const roomPhotoKeysArray = [];
      Object.keys(roomPhotos).forEach((roomKey) => {
        submitFormData.append("roomPhotos", roomPhotos[roomKey]);
        roomPhotoKeysArray.push(roomKey);
      });
      // Append room photo keys as JSON array
      if (roomPhotoKeysArray.length > 0) {
        submitFormData.append("roomPhotoKeys", JSON.stringify(roomPhotoKeysArray));
      }

      // Add bathroom photos with their keys
      const bathroomPhotoKeysArray = [];
      Object.keys(bathroomPhotos).forEach((bathroomKey) => {
        submitFormData.append("bathroomPhotos", bathroomPhotos[bathroomKey]);
        bathroomPhotoKeysArray.push(bathroomKey);
      });
      // Append bathroom photo keys as JSON array
      if (bathroomPhotoKeysArray.length > 0) {
        submitFormData.append("bathroomPhotoKeys", JSON.stringify(bathroomPhotoKeysArray));
      }

      // Add balcony photos with their keys
      const balconyPhotoKeysArray = [];
      Object.keys(balconyPhotos).forEach((balconyKey) => {
        submitFormData.append("balconyPhotos", balconyPhotos[balconyKey]);
        balconyPhotoKeysArray.push(balconyKey);
      });
      if (balconyPhotoKeysArray.length > 0) {
        submitFormData.append("balconyPhotoKeys", JSON.stringify(balconyPhotoKeysArray));
      }

      // Add compound facilities if applicable
      if (formData.compoundFacilities && formData.compoundFacilities.length > 0) {
        submitFormData.append("compoundFacilities", JSON.stringify(formData.compoundFacilities));
      }

      // Submit
      const response = await propertyOwnerAPI.createProperty(submitFormData);

      if (response.message) {
        setSuccess(true);
      }
    } catch (error) {
      console.error("Error creating property:", error);

      // Parse error message from backend (axios: error.response.data)
      let errorMessage = "Failed to create property. Please try again.";
      const data = error.response?.data;
      if (data?.errors && Array.isArray(data.errors) && data.errors.length > 0) {
        errorMessage = data.errors.join(". ");
      } else if (data?.message) {
        errorMessage = data.message;
      } else if (error.message) {
        errorMessage = error.message;
      } else if (error.error) {
        errorMessage = error.error;
      } else if (error.errors && Array.isArray(error.errors)) {
        errorMessage = error.errors.join(". ");
      }

      // Check for timeout errors
      if (errorMessage.toLowerCase().includes("timeout")) {
        errorMessage =
          "Upload timeout. Your files may be too large or your connection is slow. Please try again with smaller files or check your internet connection.";
      }

      setErrors((prev) => ({
        ...prev,
        submit: errorMessage,
      }));
    } finally {
      setLoading(false);
    }
  };

  const isApartment = formData.propertyType === "apartment";

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox py={3} position="relative">
        {/* Loading Overlay */}
        <Backdrop sx={{ color: "#fff", zIndex: (theme) => theme.zIndex.drawer + 1 }} open={loading}>
          <MDBox textAlign="center">
            <CircularProgress color="inherit" size={60} />
            <MDTypography variant="h6" color="white" mt={2}>
              Submitting Property...
            </MDTypography>
            <MDTypography variant="body2" color="white" mt={1}>
              Please wait while we process your property listing
            </MDTypography>
          </MDBox>
        </Backdrop>

        {/* Required Information Modal (Pre-Form) */}
        <Dialog
          open={showInfoModal}
          onClose={() => setShowInfoModal(false)}
          maxWidth="sm"
          fullWidth
          PaperProps={{
            sx: {
              borderRadius: 3,
              boxShadow: "0 8px 32px rgba(33, 150, 243, 0.2)",
              overflow: "hidden",
            },
          }}
        >
          <DialogTitle
            sx={{
              textAlign: "center",
              py: 4,
              px: 4,
              mb: 3,
              background: "linear-gradient(135deg, #1976d2 0%, #42a5f5 100%)",
              color: "white",
              "& .MuiTypography-root": { color: "white" },
            }}
          >
            <MDBox
              sx={{
                width: 64,
                height: 64,
                borderRadius: "50%",
                bgcolor: "rgba(255, 255, 255, 0.2)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                mx: "auto",
                mb: 2,
              }}
            >
              <Icon sx={{ fontSize: "2.5rem", color: "white !important" }}>info</Icon>
            </MDBox>
            <MDTypography variant="h5" fontWeight="bold" sx={{ color: "white !important" }}>
              Before You Start
            </MDTypography>
            <MDTypography variant="body2" sx={{ color: "white" }} mt={1}>
              Please have the following information ready
            </MDTypography>
          </DialogTitle>
          <DialogContent sx={{ px: 4, pt: 4, pb: 3 }}>
            <MDBox sx={{ display: "flex", flexDirection: "column", gap: 2.5 }}>
              <MDBox
                sx={{
                  p: 2.5,
                  mt: 2,
                  bgcolor: "grey.50",
                  borderRadius: 2,
                  border: "1px solid",
                  borderColor: "divider",
                }}
              >
                <MDTypography
                  variant="button"
                  fontWeight="bold"
                  color="info.main"
                  mb={1.5}
                  display="flex"
                  alignItems="center"
                  gap={1}
                >
                  <Icon sx={{ fontSize: "1.25rem" }}>home</Icon>
                  Basic Information
                </MDTypography>
                <MDBox component="ul" sx={{ m: 0, pl: 3, py: 0, color: "text.secondary" }}>
                  <li style={{ marginBottom: 6 }}>
                    <MDTypography variant="body2">Property title (English & Arabic)</MDTypography>
                  </li>
                  <li style={{ marginBottom: 6 }}>
                    <MDTypography variant="body2">Property type, rooms, bathrooms</MDTypography>
                  </li>
                  <li>
                    <MDTypography variant="body2">Full address, city, and country</MDTypography>
                  </li>
                </MDBox>
              </MDBox>

              <MDBox
                sx={{
                  p: 2.5,
                  bgcolor: "grey.50",
                  borderRadius: 2,
                  border: "1px solid",
                  borderColor: "divider",
                }}
              >
                <MDTypography
                  variant="button"
                  fontWeight="bold"
                  color="info.main"
                  mb={1.5}
                  display="flex"
                  alignItems="center"
                  gap={1}
                >
                  <Icon sx={{ fontSize: "1.25rem" }}>description</Icon>
                  Documents
                </MDTypography>
                <MDBox component="ul" sx={{ m: 0, pl: 3, py: 0, color: "text.secondary" }}>
                  <li style={{ marginBottom: 6 }}>
                    <MDTypography variant="body2">Property Owner ID (required)</MDTypography>
                  </li>
                  <li style={{ marginBottom: 6 }}>
                    <MDTypography variant="body2">
                      Rental or Ownership Contract (required)
                    </MDTypography>
                  </li>
                  <li>
                    <MDTypography variant="body2">Power of Attorney (optional)</MDTypography>
                  </li>
                </MDBox>
              </MDBox>

              <MDBox
                sx={{
                  p: 2.5,
                  bgcolor: "grey.50",
                  borderRadius: 2,
                  border: "1px solid",
                  borderColor: "divider",
                }}
              >
                <MDTypography
                  variant="button"
                  fontWeight="bold"
                  color="info.main"
                  mb={1.5}
                  display="flex"
                  alignItems="center"
                  gap={1}
                >
                  <Icon sx={{ fontSize: "1.25rem" }}>photo_library</Icon>
                  Photos
                </MDTypography>
                <MDBox component="ul" sx={{ m: 0, pl: 3, py: 0, color: "text.secondary" }}>
                  <li style={{ marginBottom: 6 }}>
                    <MDTypography variant="body2">Living room photo</MDTypography>
                  </li>
                  <li style={{ marginBottom: 6 }}>
                    <MDTypography variant="body2">Room photos (one per room)</MDTypography>
                  </li>
                  <li style={{ marginBottom: 6 }}>
                    <MDTypography variant="body2">Bathroom photos (one per bathroom)</MDTypography>
                  </li>
                  <li>
                    <MDTypography variant="body2">Balcony photos (if applicable)</MDTypography>
                  </li>
                </MDBox>
              </MDBox>

              <MDBox
                sx={{
                  p: 2.5,
                  bgcolor: "grey.50",
                  borderRadius: 2,
                  border: "1px solid",
                  borderColor: "divider",
                }}
              >
                <MDTypography
                  variant="button"
                  fontWeight="bold"
                  color="info.main"
                  mb={1.5}
                  display="flex"
                  alignItems="center"
                  gap={1}
                >
                  <Icon sx={{ fontSize: "1.25rem" }}>apartment</Icon>
                  Compound Info
                </MDTypography>
                <MDBox component="ul" sx={{ m: 0, pl: 3, py: 0, color: "text.secondary" }}>
                  <li>
                    <MDTypography variant="body2">
                      Compound name and facilities (if your property is in a compound)
                    </MDTypography>
                  </li>
                </MDBox>
              </MDBox>
            </MDBox>
          </DialogContent>
          <DialogActions
            sx={{
              px: 4,
              py: 3,
              gap: 2,
              flexWrap: "wrap",
              bgcolor: "grey.50",
              borderTop: "1px solid",
              borderColor: "divider",
            }}
          >
            <MDButton
              variant="outlined"
              color="secondary"
              onClick={() => navigate("/property-owner/properties")}
              sx={{ borderRadius: 2, px: 3, py: 1.25 }}
            >
              Cancel
            </MDButton>
            <MDButton
              variant="gradient"
              color="info"
              onClick={() => setShowInfoModal(false)}
              sx={{ borderRadius: 2, minWidth: 140, px: 3, py: 1.25 }}
            >
              I&apos;m Ready
            </MDButton>
          </DialogActions>
        </Dialog>

        {/* Success Modal */}
        <Dialog
          open={success}
          onClose={() => setSuccess(false)}
          maxWidth="sm"
          fullWidth
          PaperProps={{
            sx: {
              borderRadius: 3,
              boxShadow: "0 8px 32px rgba(76, 175, 80, 0.3)",
            },
          }}
        >
          <DialogTitle
            sx={{
              textAlign: "center",
              pb: 2,
              pt: 4,
              background: "linear-gradient(135deg, #4caf50 0%, #45a049 100%)",
              color: "white",
            }}
          >
            <MDBox
              sx={{
                width: 80,
                height: 80,
                borderRadius: "50%",
                bgcolor: "rgba(255, 255, 255, 0.2)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                mx: "auto",
                mb: 2,
              }}
            >
              <Icon sx={{ fontSize: "3.5rem", color: "white" }}>check_circle</Icon>
            </MDBox>
            <MDTypography variant="h4" fontWeight="bold" color="white">
              🎉 Success!
            </MDTypography>
          </DialogTitle>
          <DialogContent sx={{ pt: 4, pb: 2, mt: 3 }}>
            <MDBox textAlign="center">
              <MDTypography variant="h5" fontWeight="bold" color="success.main" mb={2}>
                Property Submitted Successfully!
              </MDTypography>
              <MDTypography variant="body1" color="text.secondary" mb={2}>
                Your property listing has been received and is now pending approval.
              </MDTypography>
              <MDBox
                sx={{
                  bgcolor: "success.lighter",
                  borderRadius: 2,
                  p: 2,
                  textAlign: "left",
                }}
              >
                <MDBox display="flex" alignItems="center" mb={1}>
                  <Icon sx={{ color: "success.main", mr: 1.5, fontSize: "1.25rem" }}>email</Icon>
                  <MDTypography variant="body2" color="text.secondary">
                    A confirmation email has been sent to your registered email address.
                  </MDTypography>
                </MDBox>
                <MDBox display="flex" alignItems="center">
                  <Icon sx={{ color: "success.main", mr: 1.5, fontSize: "1.25rem" }}>review</Icon>
                  <MDTypography variant="body2" color="text.secondary">
                    Our team will review your property and notify you once it&apos;s approved.
                  </MDTypography>
                </MDBox>
              </MDBox>
            </MDBox>
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 3, justifyContent: "center" }}>
            <MDButton
              variant="gradient"
              color="success"
              onClick={() => {
                setSuccess(false);
                navigate("/property-owner/properties");
              }}
              sx={{
                minWidth: 180,
                borderRadius: 2,
                py: 1.5,
              }}
            >
              Continue
            </MDButton>
          </DialogActions>
        </Dialog>

        {/* Error Message */}
        {errors.submit && (
          <Alert
            severity="error"
            sx={{
              mb: 3,
              borderRadius: 2,
              boxShadow: "0 4px 12px rgba(244, 67, 54, 0.3)",
              "& .MuiAlert-icon": {
                fontSize: "2rem",
              },
            }}
            icon={<Icon sx={{ fontSize: "2rem !important" }}>error</Icon>}
            action={
              <IconButton
                aria-label="close"
                color="inherit"
                size="small"
                onClick={() => setErrors((prev) => ({ ...prev, submit: "" }))}
              >
                <Icon fontSize="inherit">close</Icon>
              </IconButton>
            }
          >
            <MDTypography variant="h6" fontWeight="bold" mb={1}>
              ⚠️ Submission Failed
            </MDTypography>
            <MDTypography variant="body1" mb={1}>
              {errors.submit}
            </MDTypography>
            <MDTypography variant="body2" color="text.secondary">
              Please check your input, ensure all required fields are filled, and try again. If the
              problem persists, contact support.
            </MDTypography>
          </Alert>
        )}

        <MDBox mb={4}>
          <MDBox display="flex" alignItems="center" mb={2}>
            <MDBox
              sx={{
                width: 56,
                height: 56,
                borderRadius: 2,
                bgcolor: "grey.200",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                mr: 2,
              }}
            >
              <Icon sx={{ color: "text.secondary", fontSize: "2rem" }}>add_home</Icon>
            </MDBox>
            <MDBox>
              <MDTypography variant="h4" fontWeight="bold">
                Add New Property
              </MDTypography>
              <MDTypography variant="body2" color="text.secondary">
                Complete all steps to submit your property for approval. All fields marked with *
                are required.
              </MDTypography>
            </MDBox>
          </MDBox>
        </MDBox>

        {/* Progress Indicator - Professional Design */}
        <Card
          sx={{
            mb: 3,
            background:
              "linear-gradient(135deg, rgba(255, 255, 255, 1) 0%, rgba(250, 250, 250, 1) 100%)",
            boxShadow: "0 4px 20px rgba(0,0,0,0.08)",
            borderRadius: 3,
            overflow: "hidden",
          }}
        >
          <MDBox
            p={3}
            sx={{
              background:
                "linear-gradient(135deg, rgba(33, 150, 243, 0.05) 0%, rgba(33, 150, 243, 0.02) 100%)",
            }}
          >
            {/* Progress Bar Section */}
            <MDBox mb={3}>
              <MDBox display="flex" justifyContent="space-between" alignItems="center" mb={1.5}>
                <MDBox display="flex" alignItems="center" gap={1.5}>
                  <MDBox
                    sx={{
                      width: 48,
                      height: 48,
                      borderRadius: 2,
                      background:
                        "linear-gradient(135deg, rgba(33, 150, 243, 0.1) 0%, rgba(33, 150, 243, 0.05) 100%)",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      border: "2px solid",
                      borderColor: "info.light",
                    }}
                  >
                    <MDTypography variant="h5" fontWeight="bold" color="info.main">
                      {activeStep + 1}
                    </MDTypography>
                  </MDBox>
                  <MDBox>
                    <MDTypography variant="h6" fontWeight="bold" color="text">
                      {steps[activeStep]}
                    </MDTypography>
                    <MDTypography variant="caption" color="text.secondary">
                      Step {activeStep + 1} of {steps.length} •{" "}
                      {Math.round(((activeStep + 1) / steps.length) * 100)}% Complete
                    </MDTypography>
                  </MDBox>
                </MDBox>
                <Chip
                  label={`${Math.round(((activeStep + 1) / steps.length) * 100)}%`}
                  color="info"
                  sx={{
                    fontWeight: "bold",
                    fontSize: "0.875rem",
                    height: 32,
                    px: 1.5,
                  }}
                />
              </MDBox>
              <MDBox mt={2}>
                <LinearProgress
                  variant="determinate"
                  value={((activeStep + 1) / steps.length) * 100}
                  sx={{
                    height: 10,
                    borderRadius: 5,
                    backgroundColor: "grey.200",
                    "& .MuiLinearProgress-bar": {
                      borderRadius: 5,
                      background:
                        "linear-gradient(90deg, rgba(33, 150, 243, 1) 0%, rgba(25, 118, 210, 1) 100%)",
                      boxShadow: "0 2px 8px rgba(33, 150, 243, 0.4)",
                    },
                  }}
                />
              </MDBox>
            </MDBox>

            {/* Custom Stepper Design */}
            <MDBox
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "flex-start",
                flexWrap: "wrap",
                gap: 1,
                pt: 2,
                borderTop: "1px solid",
                borderColor: "grey.200",
              }}
            >
              {steps.map((label, index) => {
                const isActive = index === activeStep;
                const isCompleted = index < activeStep;
                const isUpcoming = index > activeStep;
                // Allow clicking on: completed steps, current step, or immediate next step
                // Disable all other future steps until current step is complete
                const canClick = isCompleted || index === activeStep || index === activeStep + 1;
                const isDisabled = isUpcoming && index > activeStep + 1;

                return (
                  <MDBox
                    key={label}
                    onClick={() => (canClick && !isDisabled ? handleStep(index) : null)}
                    sx={{
                      width: { xs: "100%", sm: "200px" },
                      maxWidth: { xs: "100%", sm: "200px" },
                      minWidth: { xs: "100%", sm: "200px" },
                      display: "flex",
                      alignItems: "center",
                      gap: 1,
                      p: 1.5,
                      borderRadius: 2,
                      cursor: canClick && !isDisabled ? "pointer" : "not-allowed",
                      transition: "all 0.2s ease-in-out",
                      opacity: isDisabled ? 0.5 : 1,
                      bgcolor: isActive
                        ? "info.main"
                        : isCompleted
                        ? "success.light"
                        : isDisabled
                        ? "grey.200"
                        : "grey.100",
                      color: isActive
                        ? "white"
                        : isCompleted
                        ? "success.dark"
                        : isDisabled
                        ? "text.disabled"
                        : "text.secondary",
                      "&:hover":
                        canClick && !isDisabled && isCompleted
                          ? {
                              bgcolor: "success.main",
                              color: "white",
                              transform: "translateY(-2px)",
                              boxShadow: "0 4px 12px rgba(76, 175, 80, 0.3)",
                            }
                          : canClick && !isDisabled && !isActive && !isCompleted
                          ? {
                              bgcolor: "grey.200",
                            }
                          : {},
                      boxShadow: isActive ? "0 4px 12px rgba(33, 150, 243, 0.3)" : "none",
                    }}
                  >
                    <MDBox
                      sx={{
                        width: 32,
                        height: 32,
                        borderRadius: "50%",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        bgcolor: isActive
                          ? "rgba(255, 255, 255, 0.2)"
                          : isCompleted
                          ? "rgba(255, 255, 255, 0.3)"
                          : "rgba(0, 0, 0, 0.1)",
                        fontWeight: "bold",
                        fontSize: "0.875rem",
                      }}
                    >
                      {isCompleted ? (
                        <Icon sx={{ fontSize: "1.25rem", color: "inherit" }}>check</Icon>
                      ) : (
                        <MDTypography variant="body2" fontWeight="bold" color="inherit">
                          {index + 1}
                        </MDTypography>
                      )}
                    </MDBox>
                    <MDTypography
                      variant="body2"
                      fontWeight={isActive ? "bold" : "medium"}
                      sx={{
                        fontSize: { xs: "0.75rem", sm: "0.875rem" },
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                      }}
                    >
                      {label}
                    </MDTypography>
                  </MDBox>
                );
              })}
            </MDBox>
          </MDBox>
        </Card>

        <form onSubmit={handleSubmit}>
          <Grid container spacing={3} sx={{ maxWidth: 1200, mx: "auto", px: { xs: 2, sm: 3 } }}>
            {/* Step 0: Basic Information */}
            {activeStep === 0 && (
              <Grid item xs={12}>
                <Card sx={{ overflow: "visible" }}>
                  <MDBox p={{ xs: 2, sm: 3 }}>
                    <MDBox
                      display="flex"
                      alignItems="center"
                      mb={3}
                      p={2}
                      borderRadius={2}
                      sx={{
                        flexWrap: "wrap",
                        gap: 1,
                        background:
                          "linear-gradient(135deg, rgba(33, 150, 243, 0.08) 0%, rgba(33, 150, 243, 0.03) 100%)",
                        borderLeft: "4px solid",
                        borderColor: "info.main",
                      }}
                    >
                      <MDBox
                        sx={{
                          width: 40,
                          height: 40,
                          borderRadius: 1.5,
                          bgcolor: "grey.200",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          flexShrink: 0,
                        }}
                      >
                        <Icon sx={{ color: "text.secondary", fontSize: "1.5rem" }}>info</Icon>
                      </MDBox>
                      <MDBox sx={{ minWidth: 0 }}>
                        <MDTypography variant="h6" fontWeight="bold">
                          Basic Information
                        </MDTypography>
                        <MDTypography variant="caption" color="text.secondary">
                          Provide property title and description in both English and Arabic
                        </MDTypography>
                      </MDBox>
                    </MDBox>

                    <Grid container spacing={2}>
                      <Grid item xs={12} md={4}>
                        <MDBox mb={2}>
                          <TextField
                            fullWidth
                            required
                            label="Property Title (English)"
                            name="titleEn"
                            value={formData.titleEn}
                            onChange={handleChange}
                            placeholder="e.g., Luxury Beach Villa"
                            error={!!errors.titleEn}
                            helperText={errors.titleEn}
                            disabled={loading}
                          />
                        </MDBox>
                      </Grid>
                      <Grid item xs={12} md={4}>
                        <MDBox mb={2}>
                          <TextField
                            fullWidth
                            required
                            label="Property Title (Arabic)"
                            name="titleAr"
                            value={formData.titleAr}
                            onChange={handleChange}
                            placeholder="مثال: فيلا شاطئية فاخرة"
                            error={!!errors.titleAr}
                            helperText={errors.titleAr}
                            disabled={loading}
                            sx={{
                              "& .MuiInputBase-input": {
                                direction: "rtl",
                                textAlign: "right",
                              },
                            }}
                          />
                        </MDBox>
                      </Grid>
                      <Grid item xs={12} md={4}>
                        <MDBox mb={2}>
                          <FormControl
                            fullWidth
                            required
                            error={!!errors.propertyType}
                            disabled={loading}
                            sx={{
                              "& .MuiOutlinedInput-root": {
                                height: "40px",
                                borderRadius: 2,
                              },
                              "& .MuiSelect-select": {
                                padding: "10px 14px",
                              },
                            }}
                          >
                            <InputLabel>Property Type</InputLabel>
                            <Select
                              name="propertyType"
                              value={formData.propertyType}
                              onChange={handleChange}
                              label="Property Type"
                              renderValue={(selected) => {
                                const selectedType = PROPERTY_TYPES.find(
                                  (type) => type.value === selected
                                );
                                if (!selectedType) return "";
                                return selectedType.label;
                              }}
                              MenuProps={{
                                PaperProps: {
                                  sx: {
                                    borderRadius: 2,
                                    mt: 1,
                                    boxShadow: "0 8px 24px rgba(0,0,0,0.12)",
                                    "& .MuiMenuItem-root": {
                                      px: 2,
                                      py: 1,
                                      "&:hover": {
                                        backgroundColor: "rgba(0, 0, 0, 0.04)",
                                      },
                                      "&.Mui-selected": {
                                        backgroundColor: "rgba(0, 0, 0, 0.06)",
                                        "&:hover": {
                                          backgroundColor: "rgba(0, 0, 0, 0.08)",
                                        },
                                      },
                                    },
                                  },
                                },
                              }}
                            >
                              {PROPERTY_TYPES.map((type) => (
                                <MenuItem key={type.value} value={type.value}>
                                  <MDTypography
                                    variant="body2"
                                    fontWeight={
                                      formData.propertyType === type.value ? "medium" : "regular"
                                    }
                                  >
                                    {type.label}
                                  </MDTypography>
                                </MenuItem>
                              ))}
                            </Select>
                          </FormControl>
                          {errors.propertyType && (
                            <MDTypography
                              variant="caption"
                              color="error"
                              ml={1.5}
                              mt={0.5}
                              display="block"
                            >
                              {errors.propertyType}
                            </MDTypography>
                          )}
                        </MDBox>
                      </Grid>

                      <Grid item xs={12} md={6}>
                        <MDBox mb={2}>
                          <TextField
                            fullWidth
                            multiline
                            rows={4}
                            label="Description (English)"
                            name="descriptionEn"
                            value={formData.descriptionEn}
                            onChange={handleChange}
                            placeholder="Describe your property in detail..."
                            disabled={loading}
                          />
                        </MDBox>
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <MDBox mb={2}>
                          <TextField
                            fullWidth
                            multiline
                            rows={4}
                            label="Description (Arabic)"
                            name="descriptionAr"
                            value={formData.descriptionAr}
                            onChange={handleChange}
                            placeholder="وصف تفصيلي للممتلكات..."
                            disabled={loading}
                            sx={{
                              "& .MuiInputBase-input": {
                                direction: "rtl",
                                textAlign: "right",
                              },
                            }}
                          />
                        </MDBox>
                      </Grid>
                    </Grid>
                  </MDBox>
                </Card>
              </Grid>
            )}

            {/* Step 2: Property Details */}
            {activeStep === 2 && (
              <Grid item xs={12}>
                <Card>
                  <MDBox p={3}>
                    <MDBox
                      display="flex"
                      alignItems="center"
                      mb={3}
                      p={2}
                      borderRadius={2}
                      sx={{
                        flexWrap: "wrap",
                        gap: 1,
                        background:
                          "linear-gradient(135deg, rgba(33, 150, 243, 0.08) 0%, rgba(33, 150, 243, 0.03) 100%)",
                        borderLeft: "4px solid",
                        borderColor: "info.main",
                      }}
                    >
                      <MDBox
                        sx={{
                          width: 40,
                          height: 40,
                          borderRadius: 1.5,
                          bgcolor: "grey.200",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          flexShrink: 0,
                        }}
                      >
                        <Icon sx={{ color: "text.secondary", fontSize: "1.5rem" }}>home</Icon>
                      </MDBox>
                      <MDBox>
                        <MDTypography variant="h6" fontWeight="bold">
                          Property Details
                        </MDTypography>
                        <MDTypography variant="caption" color="text.secondary">
                          Specify rooms, bathrooms, size, and other property specifications
                        </MDTypography>
                      </MDBox>
                    </MDBox>

                    <Grid container spacing={2}>
                      <Grid item xs={12} md={4}>
                        <MDBox mb={2}>
                          <TextField
                            fullWidth
                            required
                            type="number"
                            label="Number of Rooms"
                            name="rooms"
                            value={formData.rooms}
                            onChange={handleChange}
                            inputProps={{ min: 1 }}
                            error={!!errors.rooms}
                            helperText={errors.rooms}
                            disabled={loading}
                          />
                        </MDBox>
                      </Grid>

                      <Grid item xs={12} md={4}>
                        <MDBox mb={2}>
                          <TextField
                            fullWidth
                            required
                            type="number"
                            label="Number of Bathrooms"
                            name="bathrooms"
                            value={formData.bathrooms}
                            onChange={handleChange}
                            inputProps={{ min: 1 }}
                            error={!!errors.bathrooms}
                            helperText={errors.bathrooms}
                            disabled={loading}
                          />
                        </MDBox>
                      </Grid>

                      <Grid item xs={12} md={4}>
                        <MDBox mb={2}>
                          <TextField
                            fullWidth
                            type="number"
                            label="Number of Balconies"
                            name="balconies"
                            value={formData.balconies}
                            onChange={handleChange}
                            inputProps={{ min: 0 }}
                            error={!!errors.balconies}
                            helperText={errors.balconies || "Optional"}
                            disabled={loading}
                          />
                        </MDBox>
                      </Grid>

                      <Grid item xs={12} md={4}>
                        <MDBox mb={2}>
                          <TextField
                            fullWidth
                            required
                            type="number"
                            label="Size (m²)"
                            name="size"
                            value={formData.size}
                            onChange={handleChange}
                            inputProps={{ min: 0.01, step: 0.01 }}
                            error={!!errors.size}
                            helperText={errors.size}
                            disabled={loading}
                          />
                        </MDBox>
                      </Grid>

                      <Grid item xs={12} md={4}>
                        <MDBox mb={2}>
                          <TextField
                            fullWidth
                            required
                            type="number"
                            label="Total Floors"
                            name="floors"
                            value={formData.floors}
                            onChange={handleChange}
                            inputProps={{ min: 1 }}
                            error={!!errors.floors}
                            helperText={errors.floors}
                            disabled={loading}
                          />
                        </MDBox>
                      </Grid>

                      {/* Floor Number - Only show for apartments */}
                      {isApartment && (
                        <Grid item xs={12} md={4}>
                          <MDBox mb={2}>
                            <TextField
                              fullWidth
                              required
                              type="number"
                              label="Floor Number"
                              name="floorNumber"
                              value={formData.floorNumber}
                              onChange={handleChange}
                              inputProps={{ min: 1 }}
                              error={!!errors.floorNumber}
                              helperText={errors.floorNumber}
                              disabled={loading}
                            />
                          </MDBox>
                        </Grid>
                      )}

                      {/* New Fields */}
                      <Grid item xs={12} md={4}>
                        <MDBox mb={2}>
                          <TextField
                            fullWidth
                            required
                            type="number"
                            label="Building Age (years)"
                            name="buildingAge"
                            value={formData.buildingAge}
                            onChange={handleChange}
                            inputProps={{ min: 0 }}
                            error={!!errors.buildingAge}
                            helperText={errors.buildingAge || "Age of the building in years"}
                            disabled={loading}
                          />
                        </MDBox>
                      </Grid>

                      <Grid item xs={12} md={4}>
                        <MDBox mb={2}>
                          <TextField
                            fullWidth
                            required
                            type="number"
                            label="Furniture Age (years)"
                            name="furnitureAge"
                            value={formData.furnitureAge}
                            onChange={handleChange}
                            inputProps={{ min: 0 }}
                            error={!!errors.furnitureAge}
                            helperText={errors.furnitureAge || "Age of the furniture in years"}
                            disabled={loading}
                          />
                        </MDBox>
                      </Grid>

                      {/* Apartments per floor - Only show if not villa */}
                      {formData.propertyType && formData.propertyType !== "villa" && (
                        <Grid item xs={12} md={4}>
                          <MDBox mb={2}>
                            <TextField
                              fullWidth
                              type="number"
                              label="Apartments per Floor"
                              name="apartmentsPerFloor"
                              value={formData.apartmentsPerFloor}
                              onChange={handleChange}
                              inputProps={{ min: 1 }}
                              disabled={loading}
                            />
                          </MDBox>
                        </Grid>
                      )}

                      <Grid item xs={12} md={4}>
                        <MDBox mb={2}>
                          <FormControl fullWidth required error={!!errors.propertyView}>
                            <InputLabel>Property View</InputLabel>
                            <Select
                              name="propertyView"
                              value={formData.propertyView}
                              onChange={handleChange}
                              label="Property View *"
                              disabled={loading}
                              sx={{ minHeight: 44 }}
                            >
                              <MenuItem value="">Select</MenuItem>
                              <MenuItem value="Front View">Front View</MenuItem>
                              <MenuItem value="Back View">Back View</MenuItem>
                            </Select>
                            {errors.propertyView && (
                              <MDTypography
                                variant="caption"
                                color="error"
                                sx={{ mt: 0.5, display: "block" }}
                              >
                                {errors.propertyView}
                              </MDTypography>
                            )}
                          </FormControl>
                        </MDBox>
                      </Grid>

                      <Grid item xs={12} md={4}>
                        <MDBox mb={2}>
                          <TextField
                            fullWidth
                            label="Access Code"
                            name="accessCode"
                            value={formData.accessCode}
                            onChange={handleChange}
                            disabled={loading}
                            helperText="Access code of the apartment (if applicable)"
                          />
                        </MDBox>
                      </Grid>

                      {/* Property Status & Inspection Preferences */}
                      <Grid item xs={12}>
                        <MDTypography variant="h6" fontWeight="bold" mt={2} mb={1}>
                          Property Status & Inspection
                        </MDTypography>
                        <MDTypography
                          variant="caption"
                          color="text.secondary"
                          display="block"
                          mb={2}
                        >
                          Current state of the property and inspection preferences
                        </MDTypography>
                      </Grid>
                      <Grid item xs={12} md={4}>
                        <MDBox mb={2}>
                          <FormControl fullWidth required error={!!errors.currentPropertyState}>
                            <InputLabel>Current Property State</InputLabel>
                            <Select
                              name="currentPropertyState"
                              value={formData.currentPropertyState}
                              onChange={handleChange}
                              label="Current Property State *"
                              disabled={loading}
                              sx={{ minHeight: 44 }}
                            >
                              <MenuItem value="Rented">Rented</MenuItem>
                              <MenuItem value="Vacant">Vacant</MenuItem>
                              <MenuItem value="Available">Available</MenuItem>
                            </Select>
                            {errors.currentPropertyState && (
                              <MDTypography
                                variant="caption"
                                color="error"
                                sx={{ mt: 0.5, display: "block" }}
                              >
                                {errors.currentPropertyState}
                              </MDTypography>
                            )}
                          </FormControl>
                        </MDBox>
                      </Grid>
                      <Grid item xs={12} md={4}>
                        <MDBox mb={2}>
                          <FormControl fullWidth required error={!!errors.readyForInspector}>
                            <InputLabel>Ready for Inspector</InputLabel>
                            <Select
                              name="readyForInspector"
                              value={formData.readyForInspector}
                              onChange={handleChange}
                              label="Ready for Inspector *"
                              disabled={loading}
                              sx={{ minHeight: 44 }}
                            >
                              <MenuItem value="">Select</MenuItem>
                              <MenuItem value="true">Yes</MenuItem>
                              <MenuItem value="false">No</MenuItem>
                            </Select>
                            {errors.readyForInspector && (
                              <MDTypography
                                variant="caption"
                                color="error"
                                sx={{ mt: 0.5, display: "block" }}
                              >
                                {errors.readyForInspector}
                              </MDTypography>
                            )}
                          </FormControl>
                        </MDBox>
                      </Grid>
                      <Grid item xs={12} md={4}>
                        <MDBox mb={2}>
                          <FormControl fullWidth required error={!!errors.currentRentalProcess}>
                            <InputLabel>Current Rental Process</InputLabel>
                            <Select
                              name="currentRentalProcess"
                              value={formData.currentRentalProcess}
                              onChange={handleChange}
                              label="Current Rental Process *"
                              disabled={loading}
                              sx={{ minHeight: 44 }}
                            >
                              <MenuItem value="">Select</MenuItem>
                              <MenuItem value="Daily">Daily</MenuItem>
                              <MenuItem value="Monthly">Monthly</MenuItem>
                            </Select>
                            {errors.currentRentalProcess && (
                              <MDTypography
                                variant="caption"
                                color="error"
                                sx={{ mt: 0.5, display: "block" }}
                              >
                                {errors.currentRentalProcess}
                              </MDTypography>
                            )}
                          </FormControl>
                        </MDBox>
                      </Grid>
                      <Grid item xs={12} md={4}>
                        <MDBox mb={2}>
                          <TextField
                            fullWidth
                            required
                            type="number"
                            label="Current Rental Revenue (EGP)"
                            name="currentRentalRevenue"
                            value={formData.currentRentalRevenue}
                            onChange={handleChange}
                            inputProps={{ min: 0, step: 0.01 }}
                            error={!!errors.currentRentalRevenue}
                            helperText={
                              errors.currentRentalRevenue ||
                              "Revenue you currently get from the rental"
                            }
                            disabled={loading}
                          />
                        </MDBox>
                      </Grid>
                      {formData.readyForInspector === "true" && (
                        <Grid item xs={12} md={4}>
                          <MDBox mb={2}>
                            <FormControl
                              fullWidth
                              required
                              error={!!errors.preferredInspectionTime}
                            >
                              <InputLabel>Preferred Time for Inspection</InputLabel>
                              <Select
                                name="preferredInspectionTime"
                                value={formData.preferredInspectionTime}
                                onChange={handleChange}
                                label="Preferred Time for Inspection *"
                                disabled={loading}
                                sx={{ minHeight: 44 }}
                              >
                                <MenuItem value="">Select</MenuItem>
                                <MenuItem value="Morning (9 AM - 12 PM)">
                                  Morning (9 AM - 12 PM)
                                </MenuItem>
                                <MenuItem value="Afternoon (12 PM - 5 PM)">
                                  Afternoon (12 PM - 5 PM)
                                </MenuItem>
                                <MenuItem value="Evening (5 PM - 8 PM)">
                                  Evening (5 PM - 8 PM)
                                </MenuItem>
                                <MenuItem value="Flexible">Flexible / Any time</MenuItem>
                              </Select>
                              {errors.preferredInspectionTime && (
                                <MDTypography
                                  variant="caption"
                                  color="error"
                                  sx={{ mt: 0.5, display: "block" }}
                                >
                                  {errors.preferredInspectionTime}
                                </MDTypography>
                              )}
                            </FormControl>
                          </MDBox>
                        </Grid>
                      )}
                    </Grid>

                    {/* Living Room Photo - Always Required */}
                    <MDBox mt={3} mb={2}>
                      <MDTypography variant="h6" fontWeight="bold" mb={2}>
                        Living Room Photo (Required)
                      </MDTypography>
                      {errors.livingRoomPhoto && (
                        <MDTypography variant="caption" color="error" mb={1} display="block">
                          {errors.livingRoomPhoto}
                        </MDTypography>
                      )}
                      <MDBox
                        sx={{
                          border: "2px dashed",
                          borderColor: livingRoomPhoto ? "success.main" : "grey.300",
                          borderRadius: 2,
                          p: 2,
                          textAlign: "center",
                          bgcolor: livingRoomPhoto ? "success.lighter" : "grey.50",
                          cursor: "pointer",
                          transition: "all 0.3s",
                          "&:hover": {
                            borderColor: "info.main",
                            bgcolor: "info.lighter",
                          },
                        }}
                        onClick={() => document.getElementById("livingRoomPhotoInput")?.click()}
                      >
                        <input
                          type="file"
                          id="livingRoomPhotoInput"
                          accept="image/*"
                          style={{ display: "none" }}
                          onChange={(e) => {
                            const file = e.target.files[0];
                            if (file) {
                              setLivingRoomPhoto(file);
                              setErrors((prev) => ({ ...prev, livingRoomPhoto: "" }));
                              const reader = new FileReader();
                              reader.onload = (event) => {
                                setLivingRoomPhotoPreview(event.target.result);
                              };
                              reader.readAsDataURL(file);
                            }
                          }}
                          disabled={loading}
                        />
                        {livingRoomPhotoPreview ? (
                          <MDBox>
                            <img
                              src={livingRoomPhotoPreview}
                              alt="Living room preview"
                              style={{
                                maxWidth: "100%",
                                maxHeight: "200px",
                                borderRadius: 8,
                                marginBottom: 8,
                              }}
                            />
                            <MDButton
                              size="small"
                              color="error"
                              onClick={(e) => {
                                e.stopPropagation();
                                setLivingRoomPhoto(null);
                                setLivingRoomPhotoPreview(null);
                              }}
                            >
                              Remove
                            </MDButton>
                          </MDBox>
                        ) : (
                          <MDBox>
                            <Icon sx={{ fontSize: "3rem", color: "text.secondary", mb: 1 }}>
                              add_photo_alternate
                            </Icon>
                            <MDTypography variant="body2" color="text.secondary">
                              Click to upload living room photo
                            </MDTypography>
                          </MDBox>
                        )}
                      </MDBox>
                    </MDBox>

                    {/* Room Photos - Based on number of rooms */}
                    {formData.rooms &&
                      parseInt(formData.rooms, 10) > 0 &&
                      !Number.isNaN(parseInt(formData.rooms, 10)) && (
                        <MDBox mt={3} mb={2}>
                          <MDTypography variant="h6" fontWeight="bold" mb={2}>
                            Room Photos (One photo per room)
                          </MDTypography>
                          {errors.roomPhotos && (
                            <MDTypography variant="caption" color="error" mb={1} display="block">
                              {errors.roomPhotos}
                            </MDTypography>
                          )}
                          <Grid container spacing={2}>
                            {Array.from({ length: parseInt(formData.rooms) }, (_, i) => {
                              const roomKey = `room${i + 1}`;
                              return (
                                <Grid item xs={12} sm={6} md={4} key={roomKey}>
                                  <MDBox
                                    sx={{
                                      border: "2px dashed",
                                      borderColor: roomPhotos[roomKey]
                                        ? "success.main"
                                        : "grey.300",
                                      borderRadius: 2,
                                      p: 2,
                                      textAlign: "center",
                                      bgcolor: roomPhotos[roomKey] ? "success.lighter" : "grey.50",
                                      cursor: "pointer",
                                      transition: "all 0.3s",
                                      "&:hover": {
                                        borderColor: "info.main",
                                        bgcolor: "info.lighter",
                                      },
                                    }}
                                    onClick={() =>
                                      document.getElementById(`roomPhotoInput${i + 1}`)?.click()
                                    }
                                  >
                                    <input
                                      type="file"
                                      id={`roomPhotoInput${i + 1}`}
                                      accept="image/*"
                                      style={{ display: "none" }}
                                      onChange={(e) => {
                                        const file = e.target.files[0];
                                        if (file) {
                                          setRoomPhotos((prev) => ({ ...prev, [roomKey]: file }));
                                          setErrors((prev) => ({ ...prev, roomPhotos: "" }));
                                          const reader = new FileReader();
                                          reader.onload = (event) => {
                                            setRoomPhotoPreviews((prev) => ({
                                              ...prev,
                                              [roomKey]: event.target.result,
                                            }));
                                          };
                                          reader.readAsDataURL(file);
                                        }
                                      }}
                                      disabled={loading}
                                    />
                                    {roomPhotoPreviews[roomKey] ? (
                                      <MDBox>
                                        <img
                                          src={roomPhotoPreviews[roomKey]}
                                          alt={`Room ${i + 1} preview`}
                                          style={{
                                            maxWidth: "100%",
                                            maxHeight: "150px",
                                            borderRadius: 8,
                                            marginBottom: 8,
                                          }}
                                        />
                                        <MDButton
                                          size="small"
                                          color="error"
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            setRoomPhotos((prev) => {
                                              const newPhotos = { ...prev };
                                              delete newPhotos[roomKey];
                                              return newPhotos;
                                            });
                                            setRoomPhotoPreviews((prev) => {
                                              const newPreviews = { ...prev };
                                              delete newPreviews[roomKey];
                                              return newPreviews;
                                            });
                                          }}
                                        >
                                          Remove
                                        </MDButton>
                                      </MDBox>
                                    ) : (
                                      <MDBox>
                                        <Icon
                                          sx={{ fontSize: "2rem", color: "text.secondary", mb: 1 }}
                                        >
                                          add_photo_alternate
                                        </Icon>
                                        <MDTypography
                                          variant="caption"
                                          color="text.secondary"
                                          display="block"
                                        >
                                          Room {i + 1}
                                        </MDTypography>
                                      </MDBox>
                                    )}
                                  </MDBox>
                                </Grid>
                              );
                            })}
                          </Grid>
                        </MDBox>
                      )}

                    {/* Bathroom Photos - Based on number of bathrooms */}
                    {formData.bathrooms &&
                      parseInt(formData.bathrooms, 10) > 0 &&
                      !Number.isNaN(parseInt(formData.bathrooms, 10)) && (
                        <MDBox mt={3} mb={2}>
                          <MDTypography variant="h6" fontWeight="bold" mb={2}>
                            Bathroom Photos (One photo per bathroom)
                          </MDTypography>
                          {errors.bathroomPhotos && (
                            <MDTypography variant="caption" color="error" mb={1} display="block">
                              {errors.bathroomPhotos}
                            </MDTypography>
                          )}
                          <Grid container spacing={2}>
                            {Array.from({ length: parseInt(formData.bathrooms) }, (_, i) => {
                              const bathroomKey = `bathroom${i + 1}`;
                              return (
                                <Grid item xs={12} sm={6} md={4} key={bathroomKey}>
                                  <MDBox
                                    sx={{
                                      border: "2px dashed",
                                      borderColor: bathroomPhotos[bathroomKey]
                                        ? "success.main"
                                        : "grey.300",
                                      borderRadius: 2,
                                      p: 2,
                                      textAlign: "center",
                                      bgcolor: bathroomPhotos[bathroomKey]
                                        ? "success.lighter"
                                        : "grey.50",
                                      cursor: "pointer",
                                      transition: "all 0.3s",
                                      "&:hover": {
                                        borderColor: "info.main",
                                        bgcolor: "info.lighter",
                                      },
                                    }}
                                    onClick={() =>
                                      document.getElementById(`bathroomPhotoInput${i + 1}`)?.click()
                                    }
                                  >
                                    <input
                                      type="file"
                                      id={`bathroomPhotoInput${i + 1}`}
                                      accept="image/*"
                                      style={{ display: "none" }}
                                      onChange={(e) => {
                                        const file = e.target.files[0];
                                        if (file) {
                                          setBathroomPhotos((prev) => ({
                                            ...prev,
                                            [bathroomKey]: file,
                                          }));
                                          setErrors((prev) => ({ ...prev, bathroomPhotos: "" }));
                                          const reader = new FileReader();
                                          reader.onload = (event) => {
                                            setBathroomPhotoPreviews((prev) => ({
                                              ...prev,
                                              [bathroomKey]: event.target.result,
                                            }));
                                          };
                                          reader.readAsDataURL(file);
                                        }
                                      }}
                                      disabled={loading}
                                    />
                                    {bathroomPhotoPreviews[bathroomKey] ? (
                                      <MDBox>
                                        <img
                                          src={bathroomPhotoPreviews[bathroomKey]}
                                          alt={`Bathroom ${i + 1} preview`}
                                          style={{
                                            maxWidth: "100%",
                                            maxHeight: "150px",
                                            borderRadius: 8,
                                            marginBottom: 8,
                                          }}
                                        />
                                        <MDButton
                                          size="small"
                                          color="error"
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            setBathroomPhotos((prev) => {
                                              const newPhotos = { ...prev };
                                              delete newPhotos[bathroomKey];
                                              return newPhotos;
                                            });
                                            setBathroomPhotoPreviews((prev) => {
                                              const newPreviews = { ...prev };
                                              delete newPreviews[bathroomKey];
                                              return newPreviews;
                                            });
                                          }}
                                        >
                                          Remove
                                        </MDButton>
                                      </MDBox>
                                    ) : (
                                      <MDBox>
                                        <Icon
                                          sx={{ fontSize: "2rem", color: "text.secondary", mb: 1 }}
                                        >
                                          add_photo_alternate
                                        </Icon>
                                        <MDTypography
                                          variant="caption"
                                          color="text.secondary"
                                          display="block"
                                        >
                                          Bathroom {i + 1}
                                        </MDTypography>
                                      </MDBox>
                                    )}
                                  </MDBox>
                                </Grid>
                              );
                            })}
                          </Grid>
                        </MDBox>
                      )}

                    {/* Balcony Photos - Based on number of balconies */}
                    {formData.balconies &&
                      parseInt(formData.balconies, 10) > 0 &&
                      !Number.isNaN(parseInt(formData.balconies, 10)) && (
                        <MDBox mt={3} mb={2}>
                          <MDTypography variant="h6" fontWeight="bold" mb={2}>
                            Balcony Photos (One photo per balcony – required)
                          </MDTypography>
                          {errors.balconyPhotos && (
                            <MDTypography variant="caption" color="error" mb={1} display="block">
                              {errors.balconyPhotos}
                            </MDTypography>
                          )}
                          <Grid container spacing={2}>
                            {Array.from({ length: parseInt(formData.balconies) }, (_, i) => {
                              const balconyKey = `balcony${i + 1}`;
                              return (
                                <Grid item xs={12} sm={6} md={4} key={balconyKey}>
                                  <MDBox
                                    sx={{
                                      border: "2px dashed",
                                      borderColor: balconyPhotos[balconyKey]
                                        ? "success.main"
                                        : "grey.300",
                                      borderRadius: 2,
                                      p: 2,
                                      textAlign: "center",
                                      bgcolor: balconyPhotos[balconyKey]
                                        ? "success.lighter"
                                        : "grey.50",
                                      cursor: "pointer",
                                      transition: "all 0.3s",
                                      "&:hover": {
                                        borderColor: "info.main",
                                        bgcolor: "info.lighter",
                                      },
                                    }}
                                    onClick={() =>
                                      document.getElementById(`balconyPhotoInput${i + 1}`)?.click()
                                    }
                                  >
                                    <input
                                      type="file"
                                      id={`balconyPhotoInput${i + 1}`}
                                      accept="image/*"
                                      style={{ display: "none" }}
                                      onChange={(e) => {
                                        const file = e.target.files[0];
                                        if (file) {
                                          setBalconyPhotos((prev) => ({
                                            ...prev,
                                            [balconyKey]: file,
                                          }));
                                          setErrors((prev) => ({ ...prev, balconyPhotos: "" }));
                                          const reader = new FileReader();
                                          reader.onload = (event) => {
                                            setBalconyPhotoPreviews((prev) => ({
                                              ...prev,
                                              [balconyKey]: event.target.result,
                                            }));
                                          };
                                          reader.readAsDataURL(file);
                                        }
                                      }}
                                      disabled={loading}
                                    />
                                    {balconyPhotoPreviews[balconyKey] ? (
                                      <MDBox>
                                        <img
                                          src={balconyPhotoPreviews[balconyKey]}
                                          alt={`Balcony ${i + 1} preview`}
                                          style={{
                                            maxWidth: "100%",
                                            maxHeight: "150px",
                                            borderRadius: 8,
                                            marginBottom: 8,
                                          }}
                                        />
                                        <MDButton
                                          size="small"
                                          color="error"
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            setBalconyPhotos((prev) => {
                                              const newPhotos = { ...prev };
                                              delete newPhotos[balconyKey];
                                              return newPhotos;
                                            });
                                            setBalconyPhotoPreviews((prev) => {
                                              const newPreviews = { ...prev };
                                              delete newPreviews[balconyKey];
                                              return newPreviews;
                                            });
                                          }}
                                        >
                                          Remove
                                        </MDButton>
                                      </MDBox>
                                    ) : (
                                      <MDBox>
                                        <Icon
                                          sx={{ fontSize: "2rem", color: "text.secondary", mb: 1 }}
                                        >
                                          add_photo_alternate
                                        </Icon>
                                        <MDTypography
                                          variant="caption"
                                          color="text.secondary"
                                          display="block"
                                        >
                                          Balcony {i + 1}
                                        </MDTypography>
                                      </MDBox>
                                    )}
                                  </MDBox>
                                </Grid>
                              );
                            })}
                          </Grid>
                        </MDBox>
                      )}
                  </MDBox>
                </Card>
              </Grid>
            )}

            {/* Step 1: Location & Address */}
            {activeStep === 1 && (
              <Grid item xs={12}>
                <Card>
                  <MDBox p={3}>
                    <MDBox
                      display="flex"
                      alignItems="center"
                      mb={3}
                      p={2}
                      borderRadius={2}
                      sx={{
                        flexWrap: "wrap",
                        gap: 1,
                        background:
                          "linear-gradient(135deg, rgba(33, 150, 243, 0.08) 0%, rgba(33, 150, 243, 0.03) 100%)",
                        borderLeft: "4px solid",
                        borderColor: "info.main",
                      }}
                    >
                      <MDBox
                        sx={{
                          width: 40,
                          height: 40,
                          borderRadius: 1.5,
                          bgcolor: "grey.200",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          flexShrink: 0,
                        }}
                      >
                        <Icon sx={{ color: "text.secondary", fontSize: "1.5rem" }}>
                          location_on
                        </Icon>
                      </MDBox>
                      <MDBox>
                        <MDTypography variant="h6" fontWeight="bold">
                          Location
                        </MDTypography>
                        <MDTypography variant="caption" color="text.secondary">
                          Select country and city in English - Arabic translation will be automatic
                        </MDTypography>
                      </MDBox>
                    </MDBox>

                    <Grid container spacing={2}>
                      <Grid item xs={12} md={6}>
                        <MDBox mb={2}>
                          <TextField
                            fullWidth
                            required
                            label="Address (English)"
                            name="addressEn"
                            value={formData.addressEn}
                            onChange={handleChange}
                            placeholder="Street address"
                            error={!!errors.addressEn}
                            helperText={errors.addressEn}
                            disabled={loading}
                          />
                        </MDBox>
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <MDBox mb={2}>
                          <TextField
                            fullWidth
                            required
                            label="Address (Arabic)"
                            name="addressAr"
                            value={formData.addressAr}
                            onChange={handleChange}
                            placeholder="عنوان الشارع"
                            error={!!errors.addressAr}
                            helperText={errors.addressAr}
                            disabled={loading}
                            sx={{
                              "& .MuiInputBase-input": {
                                direction: "rtl",
                                textAlign: "right",
                              },
                            }}
                          />
                        </MDBox>
                      </Grid>

                      <Grid item xs={12} md={6}>
                        <MDBox mb={2}>
                          <Autocomplete
                            options={COUNTRIES}
                            value={formData.countryEn || null}
                            onChange={(event, newValue) => {
                              const arabicTranslation = newValue
                                ? getArabicTranslation(newValue, COUNTRY_TRANSLATIONS)
                                : "";
                              setFormData((prev) => ({
                                ...prev,
                                countryEn: newValue || "",
                                countryAr: arabicTranslation,
                                cityEn: "", // Reset city when country changes
                                cityAr: "", // Reset city Arabic when country changes
                              }));
                              if (errors.countryEn) {
                                setErrors((prev) => ({ ...prev, countryEn: "" }));
                              }
                            }}
                            disabled={loading}
                            renderInput={(params) => (
                              <TextField
                                {...params}
                                label="Country"
                                placeholder="Select or type country name"
                                error={!!errors.countryEn}
                                helperText={
                                  errors.countryEn ||
                                  "Arabic translation will be filled automatically"
                                }
                                InputProps={{
                                  ...params.InputProps,
                                  startAdornment: (
                                    <>
                                      <Icon sx={{ mr: 1, color: "text.secondary" }}>public</Icon>
                                      {params.InputProps.startAdornment}
                                    </>
                                  ),
                                }}
                              />
                            )}
                            PaperComponent={(props) => (
                              <Paper
                                {...props}
                                sx={{
                                  borderRadius: 2,
                                  mt: 1,
                                  boxShadow: "0 8px 24px rgba(0,0,0,0.12)",
                                }}
                              />
                            )}
                            sx={{
                              "& .MuiOutlinedInput-root": {
                                borderRadius: 2,
                                "&:hover fieldset": {
                                  borderColor: "info.main",
                                },
                                "&.Mui-focused fieldset": {
                                  borderColor: "info.main",
                                  borderWidth: 2,
                                },
                              },
                            }}
                          />
                        </MDBox>
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <MDBox mb={2}>
                          <MDBox display="flex" alignItems="center" mb={0.5}>
                            <MDTypography variant="caption" color="text.secondary" sx={{ mr: 1 }}>
                              Country (Arabic)
                            </MDTypography>
                            {formData.countryEn && (
                              <Chip
                                label="Auto-filled"
                                size="small"
                                color="success"
                                icon={
                                  <Icon sx={{ fontSize: "0.875rem !important" }}>translate</Icon>
                                }
                                sx={{ height: 20, fontSize: "0.65rem" }}
                              />
                            )}
                          </MDBox>
                          <TextField
                            fullWidth
                            name="countryAr"
                            value={formData.countryAr}
                            onChange={handleChange}
                            placeholder="سيتم ملؤه تلقائياً"
                            disabled={loading || !!formData.countryEn}
                            sx={{
                              "& .MuiInputBase-input": {
                                direction: "rtl",
                                textAlign: "right",
                              },
                              bgcolor: formData.countryEn ? "success.lighter" : "transparent",
                              "& .MuiOutlinedInput-root": {
                                "& fieldset": {
                                  borderColor: formData.countryEn
                                    ? "success.main"
                                    : "rgba(0, 0, 0, 0.23)",
                                },
                              },
                            }}
                            helperText={
                              formData.countryEn
                                ? "✓ Auto-translated from English"
                                : "Select country in English first"
                            }
                          />
                        </MDBox>
                      </Grid>

                      <Grid item xs={12} md={6}>
                        <MDBox mb={2}>
                          <Autocomplete
                            options={
                              formData.countryEn && CITIES_BY_COUNTRY[formData.countryEn]
                                ? CITIES_BY_COUNTRY[formData.countryEn]
                                : getAllCities()
                            }
                            value={formData.cityEn || null}
                            onChange={(event, newValue) => {
                              const arabicTranslation = newValue
                                ? getArabicTranslation(newValue, CITY_TRANSLATIONS)
                                : "";
                              setFormData((prev) => ({
                                ...prev,
                                cityEn: newValue || "",
                                cityAr: arabicTranslation,
                              }));
                              if (errors.cityEn) {
                                setErrors((prev) => ({ ...prev, cityEn: "" }));
                              }
                            }}
                            disabled={loading || !formData.countryEn}
                            renderInput={(params) => (
                              <TextField
                                {...params}
                                label="City"
                                placeholder={
                                  formData.countryEn
                                    ? "Select or type city name"
                                    : "Select country first"
                                }
                                error={!!errors.cityEn}
                                helperText={
                                  errors.cityEn ||
                                  (formData.countryEn
                                    ? "Arabic translation will be filled automatically"
                                    : "Please select a country first")
                                }
                                InputProps={{
                                  ...params.InputProps,
                                  startAdornment: (
                                    <>
                                      <Icon sx={{ mr: 1, color: "text.secondary" }}>
                                        location_city
                                      </Icon>
                                      {params.InputProps.startAdornment}
                                    </>
                                  ),
                                }}
                              />
                            )}
                            PaperComponent={(props) => (
                              <Paper
                                {...props}
                                sx={{
                                  borderRadius: 2,
                                  mt: 1,
                                  boxShadow: "0 8px 24px rgba(0,0,0,0.12)",
                                }}
                              />
                            )}
                            sx={{
                              "& .MuiOutlinedInput-root": {
                                borderRadius: 2,
                                "&:hover fieldset": {
                                  borderColor: "info.main",
                                },
                                "&.Mui-focused fieldset": {
                                  borderColor: "info.main",
                                  borderWidth: 2,
                                },
                              },
                            }}
                            filterOptions={(options, params) => {
                              const filtered = options.filter((option) =>
                                option.toLowerCase().includes(params.inputValue.toLowerCase())
                              );
                              return filtered;
                            }}
                          />
                        </MDBox>
                      </Grid>

                      <Grid item xs={12} md={6}>
                        <MDBox mb={2}>
                          <MDBox display="flex" alignItems="center" mb={0.5}>
                            <MDTypography variant="caption" color="text.secondary" sx={{ mr: 1 }}>
                              City (Arabic)
                            </MDTypography>
                            {formData.cityEn && (
                              <Chip
                                label="Auto-filled"
                                size="small"
                                color="success"
                                icon={
                                  <Icon sx={{ fontSize: "0.875rem !important" }}>translate</Icon>
                                }
                                sx={{ height: 20, fontSize: "0.65rem" }}
                              />
                            )}
                          </MDBox>
                          <TextField
                            fullWidth
                            name="cityAr"
                            value={formData.cityAr}
                            onChange={handleChange}
                            placeholder="سيتم ملؤه تلقائياً"
                            disabled={loading || !!formData.cityEn}
                            sx={{
                              "& .MuiInputBase-input": {
                                direction: "rtl",
                                textAlign: "right",
                              },
                              bgcolor: formData.cityEn ? "success.lighter" : "transparent",
                              "& .MuiOutlinedInput-root": {
                                "& fieldset": {
                                  borderColor: formData.cityEn
                                    ? "success.main"
                                    : "rgba(0, 0, 0, 0.23)",
                                },
                              },
                            }}
                            helperText={
                              formData.cityEn
                                ? "✓ Auto-translated from English"
                                : "Select city in English first"
                            }
                          />
                        </MDBox>
                      </Grid>

                      <Grid item xs={12} md={6}>
                        <MDBox mb={2}>
                          <TextField
                            fullWidth
                            required
                            label="Google Map Link"
                            name="googleMapLink"
                            value={formData.googleMapLink}
                            onChange={handleChange}
                            placeholder="https://maps.google.com/..."
                            error={!!errors.googleMapLink}
                            helperText={errors.googleMapLink}
                            disabled={loading}
                          />
                        </MDBox>
                      </Grid>

                      <Grid item xs={12} md={6}>
                        <MDBox mb={2}>
                          <TextField
                            fullWidth
                            label="Landmark (English)"
                            name="landmark"
                            value={formData.landmark}
                            onChange={handleChange}
                            placeholder="e.g., Near the mall, Next to the park"
                            disabled={loading}
                            helperText="Nearby landmark or point of reference"
                          />
                        </MDBox>
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <MDBox mb={2}>
                          <TextField
                            fullWidth
                            label="Landmark (Arabic)"
                            name="landmarkAr"
                            value={formData.landmarkAr}
                            onChange={handleChange}
                            placeholder="مثال: بالقرب من المول، بجوار الحديقة"
                            disabled={loading}
                            helperText="معلم قريب أو نقطة مرجعية"
                          />
                        </MDBox>
                      </Grid>
                    </Grid>
                  </MDBox>
                </Card>
              </Grid>
            )}

            {/* Step 3: Compound Information */}
            {activeStep === 3 && (
              <Grid item xs={12}>
                <Card>
                  <MDBox p={3}>
                    <MDBox
                      display="flex"
                      alignItems="center"
                      mb={3}
                      p={2}
                      borderRadius={2}
                      sx={{
                        flexWrap: "wrap",
                        gap: 1,
                        background:
                          "linear-gradient(135deg, rgba(33, 150, 243, 0.08) 0%, rgba(33, 150, 243, 0.03) 100%)",
                        borderLeft: "4px solid",
                        borderColor: "info.main",
                      }}
                    >
                      <MDBox
                        sx={{
                          width: 40,
                          height: 40,
                          borderRadius: 1.5,
                          bgcolor: "grey.200",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          flexShrink: 0,
                        }}
                      >
                        <Icon sx={{ color: "text.secondary", fontSize: "1.5rem" }}>apartment</Icon>
                      </MDBox>
                      <MDBox>
                        <MDTypography variant="h6" fontWeight="bold">
                          Compound Information
                        </MDTypography>
                        <MDTypography variant="caption" color="text.secondary">
                          Provide compound details if your property is inside a compound
                        </MDTypography>
                      </MDBox>
                    </MDBox>

                    <Grid container spacing={2}>
                      <Grid item xs={12}>
                        <FormControlLabel
                          control={
                            <Switch
                              checked={formData.isInCompound}
                              onChange={(e) =>
                                setFormData((prev) => ({ ...prev, isInCompound: e.target.checked }))
                              }
                              disabled={loading}
                            />
                          }
                          label="Is this property inside a compound?"
                        />
                      </Grid>

                      {formData.isInCompound && (
                        <>
                          <Grid item xs={12} md={6}>
                            <MDBox mb={2}>
                              <TextField
                                fullWidth
                                required
                                label="Compound Name (English)"
                                name="compoundName"
                                value={formData.compoundName}
                                onChange={handleChange}
                                placeholder="e.g., Palm Hills, New Cairo Compound"
                                error={!!errors.compoundName}
                                helperText={errors.compoundName}
                                disabled={loading}
                              />
                            </MDBox>
                          </Grid>
                          <Grid item xs={12} md={6}>
                            <MDBox mb={2}>
                              <TextField
                                fullWidth
                                label="Compound Name (Arabic)"
                                name="compoundNameAr"
                                value={formData.compoundNameAr}
                                onChange={handleChange}
                                placeholder="مثال: بالم هيلز، كمبوند القاهرة الجديدة"
                                disabled={loading}
                              />
                            </MDBox>
                          </Grid>

                          <Grid item xs={12}>
                            <MDBox mb={2}>
                              <MDTypography variant="body2" fontWeight="medium" mb={1.5}>
                                Compound Facilities
                              </MDTypography>
                              <MDBox display="flex" flexWrap="wrap" gap={1}>
                                {COMPOUND_FACILITIES.map((facility) => {
                                  const facilityEn =
                                    typeof facility === "object" ? facility.en : facility;
                                  const isSelected = formData.compoundFacilities?.some((f) => {
                                    const fEn = typeof f === "object" ? f.en : f;
                                    return fEn === facilityEn;
                                  });
                                  return (
                                    <Chip
                                      key={facilityEn}
                                      label={facilityEn}
                                      onClick={() => handleCompoundFacilityChange(facility)}
                                      color={isSelected ? "primary" : "default"}
                                      variant={isSelected ? "filled" : "outlined"}
                                      sx={{ cursor: "pointer" }}
                                      disabled={loading}
                                    />
                                  );
                                })}
                              </MDBox>
                            </MDBox>
                          </Grid>
                        </>
                      )}
                    </Grid>
                  </MDBox>
                </Card>
              </Grid>
            )}

            {/* Step 4: WiFi & Utilities */}
            {activeStep === 4 && (
              <Grid item xs={12}>
                <Card>
                  <MDBox p={3}>
                    <MDBox
                      display="flex"
                      alignItems="center"
                      mb={3}
                      p={2}
                      borderRadius={2}
                      sx={{
                        flexWrap: "wrap",
                        gap: 1,
                        background:
                          "linear-gradient(135deg, rgba(33, 150, 243, 0.08) 0%, rgba(33, 150, 243, 0.03) 100%)",
                        borderLeft: "4px solid",
                        borderColor: "info.main",
                      }}
                    >
                      <MDBox
                        sx={{
                          width: 40,
                          height: 40,
                          borderRadius: 1.5,
                          bgcolor: "grey.200",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          flexShrink: 0,
                        }}
                      >
                        <Icon sx={{ color: "text.secondary", fontSize: "1.5rem" }}>wifi</Icon>
                      </MDBox>
                      <MDBox>
                        <MDTypography variant="h6" fontWeight="bold">
                          WiFi & Utilities
                        </MDTypography>
                        <MDTypography variant="caption" color="text.secondary">
                          Provide WiFi credentials and utility information
                        </MDTypography>
                      </MDBox>
                    </MDBox>

                    <Grid container spacing={2}>
                      <Grid item xs={12} md={6}>
                        <MDBox mb={2}>
                          <TextField
                            fullWidth
                            label="WiFi Name (SSID)"
                            name="wifiName"
                            value={formData.wifiName}
                            onChange={handleChange}
                            placeholder="e.g., MyProperty_WiFi"
                            disabled={loading}
                          />
                        </MDBox>
                      </Grid>

                      <Grid item xs={12} md={6}>
                        <MDBox mb={2}>
                          <TextField
                            fullWidth
                            type="password"
                            label="WiFi Password"
                            name="wifiPassword"
                            value={formData.wifiPassword}
                            onChange={handleChange}
                            placeholder="Enter WiFi password"
                            disabled={loading}
                          />
                        </MDBox>
                      </Grid>

                      <Grid item xs={12} md={6}>
                        <MDBox mb={2}>
                          <FormControl fullWidth required error={!!errors.electricityMeterType}>
                            <InputLabel>Electricity Meter Type</InputLabel>
                            <Select
                              name="electricityMeterType"
                              value={formData.electricityMeterType}
                              onChange={handleChange}
                              label="Electricity Meter Type *"
                              disabled={loading}
                              sx={{ minHeight: 56 }}
                            >
                              <MenuItem value="">Select</MenuItem>
                              <MenuItem value="Prepaid Meter (Card Recharge)">
                                Prepaid Meter (Card Recharge)
                              </MenuItem>
                              <MenuItem value="Postpaid Meter (Monthly Payment)">
                                Postpaid Meter (Monthly Payment)
                              </MenuItem>
                            </Select>
                            {errors.electricityMeterType && (
                              <MDTypography
                                variant="caption"
                                color="error"
                                sx={{ mt: 0.5, display: "block" }}
                              >
                                {errors.electricityMeterType}
                              </MDTypography>
                            )}
                          </FormControl>
                        </MDBox>
                      </Grid>

                      <Grid item xs={12} md={6}>
                        <MDBox mb={2}>
                          <FormControl fullWidth required error={!!errors.waterMeterType}>
                            <InputLabel>Water Meter Type</InputLabel>
                            <Select
                              name="waterMeterType"
                              value={formData.waterMeterType}
                              onChange={handleChange}
                              label="Water Meter Type *"
                              disabled={loading}
                              sx={{ minHeight: 56 }}
                            >
                              <MenuItem value="">Select</MenuItem>
                              <MenuItem value="Prepaid Meter (Card Recharge)">
                                Prepaid Meter (Card Recharge)
                              </MenuItem>
                              <MenuItem value="Postpaid Meter (Monthly Payment)">
                                Postpaid Meter (Monthly Payment)
                              </MenuItem>
                            </Select>
                            {errors.waterMeterType && (
                              <MDTypography
                                variant="caption"
                                color="error"
                                sx={{ mt: 0.5, display: "block" }}
                              >
                                {errors.waterMeterType}
                              </MDTypography>
                            )}
                          </FormControl>
                        </MDBox>
                      </Grid>
                    </Grid>
                  </MDBox>
                </Card>
              </Grid>
            )}

            {/* Step 5: Amenities & Criteria */}
            {activeStep === 5 && (
              <>
                <Grid item xs={12}>
                  <Card>
                    <MDBox p={3}>
                      <MDBox
                        display="flex"
                        alignItems="center"
                        mb={3}
                        p={2}
                        borderRadius={2}
                        sx={{
                          flexWrap: "wrap",
                          gap: 1,
                          background:
                            "linear-gradient(135deg, rgba(33, 150, 243, 0.08) 0%, rgba(33, 150, 243, 0.03) 100%)",
                          borderLeft: "4px solid",
                          borderColor: "info.main",
                        }}
                      >
                        <MDBox
                          sx={{
                            width: 40,
                            height: 40,
                            borderRadius: 1.5,
                            bgcolor: "grey.200",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            flexShrink: 0,
                          }}
                        >
                          <Icon sx={{ color: "text.secondary", fontSize: "1.5rem" }}>star</Icon>
                        </MDBox>
                        <MDBox>
                          <MDTypography variant="h6" fontWeight="bold">
                            Amenities (select at least one) *
                          </MDTypography>
                          <MDTypography variant="caption" color="text.secondary">
                            Select all amenities available in your property
                          </MDTypography>
                          {errors.amenities && (
                            <MDTypography
                              variant="caption"
                              color="error"
                              display="block"
                              sx={{ mt: 0.5 }}
                            >
                              {errors.amenities}
                            </MDTypography>
                          )}
                        </MDBox>
                      </MDBox>

                      {loadingData ? (
                        <MDBox display="flex" justifyContent="center" p={3}>
                          <CircularProgress size={24} />
                        </MDBox>
                      ) : (
                        <MDBox display="flex" flexWrap="wrap" gap={1}>
                          {amenitiesList.map((amenity) => {
                            const amenityEn = amenity.name?.en || amenity.en || amenity;
                            const amenityAr = amenity.name?.ar || amenity.ar || "";
                            const isSelected = formData.amenities.some((a) => {
                              const aEn = typeof a === "object" ? a.en || a.name?.en : a;
                              return aEn === amenityEn;
                            });
                            return (
                              <Chip
                                key={amenity._id || amenityEn}
                                label={amenityEn}
                                onClick={() => handleAmenityToggle(amenity)}
                                color={isSelected ? "primary" : "default"}
                                variant={isSelected ? "filled" : "outlined"}
                                sx={{ cursor: "pointer" }}
                                disabled={loading}
                              />
                            );
                          })}
                        </MDBox>
                      )}
                    </MDBox>
                  </Card>
                </Grid>

                {/* Property Criteria Checklist */}
                <Grid item xs={12}>
                  <Card>
                    <MDBox p={3}>
                      <MDBox
                        display="flex"
                        alignItems="center"
                        mb={3}
                        p={2}
                        borderRadius={2}
                        sx={{
                          flexWrap: "wrap",
                          gap: 1,
                          background:
                            "linear-gradient(135deg, rgba(33, 150, 243, 0.08) 0%, rgba(33, 150, 243, 0.03) 100%)",
                          borderLeft: "4px solid",
                          borderColor: "info.main",
                        }}
                      >
                        <MDBox
                          sx={{
                            width: 40,
                            height: 40,
                            borderRadius: 1.5,
                            bgcolor: "grey.200",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            flexShrink: 0,
                          }}
                        >
                          <Icon sx={{ color: "text.secondary", fontSize: "1.5rem" }}>
                            checklist
                          </Icon>
                        </MDBox>
                        <MDBox>
                          <MDTypography variant="h6" fontWeight="bold">
                            Property Criteria Checklist (select at least one) *
                          </MDTypography>
                          <MDTypography variant="caption" color="text.secondary">
                            Select all criteria that apply to your property. The inspector will
                            verify these during inspection.
                          </MDTypography>
                          {errors.criteria && (
                            <MDTypography
                              variant="caption"
                              color="error"
                              display="block"
                              sx={{ mt: 0.5 }}
                            >
                              {errors.criteria}
                            </MDTypography>
                          )}
                        </MDBox>
                      </MDBox>

                      {loadingData ? (
                        <MDBox display="flex" justifyContent="center" p={3}>
                          <CircularProgress size={24} />
                        </MDBox>
                      ) : (
                        <>
                          {/* Group criteria by category */}
                          {Object.entries(
                            criteriaList.reduce((acc, criterion) => {
                              const category =
                                criterion.category?.en || criterion.category || "General";
                              if (!acc[category]) {
                                acc[category] = [];
                              }
                              acc[category].push(criterion);
                              return acc;
                            }, {})
                          ).map(([category, criteria]) => (
                            <MDBox key={category} mb={3}>
                              <MDTypography
                                variant="subtitle2"
                                fontWeight="bold"
                                mb={1.5}
                                color="info.main"
                              >
                                {category}
                              </MDTypography>
                              <MDBox display="flex" flexWrap="wrap" gap={1} alignItems="center">
                                {criteria.map((criterion) => {
                                  const criterionName = criterion.name?.en || criterion.name || "";
                                  const isQuantifiable = criterion.quantifiable === true;
                                  const selectedCriterion = formData.criteria.find((c) => {
                                    const cName =
                                      typeof c.name === "object"
                                        ? c.name.en || c.name
                                        : c.name || c;
                                    return cName === criterionName;
                                  });
                                  const isSelected = !!selectedCriterion;
                                  const quantity = selectedCriterion?.quantity ?? 1;
                                  return (
                                    <MDBox
                                      key={criterion._id || criterionName}
                                      display="flex"
                                      alignItems="center"
                                      gap={1}
                                      mb={1}
                                    >
                                      <Chip
                                        label={criterionName}
                                        onClick={() => handleCriteriaToggle(criterion)}
                                        color={isSelected ? "success" : "default"}
                                        variant={isSelected ? "filled" : "outlined"}
                                        icon={isSelected ? <Icon>check</Icon> : <Icon>add</Icon>}
                                        sx={{
                                          cursor: "pointer",
                                          "&:hover": {
                                            backgroundColor: isSelected
                                              ? "success.dark"
                                              : "grey.100",
                                          },
                                        }}
                                        disabled={loading}
                                      />
                                      {isQuantifiable && isSelected && (
                                        <TextField
                                          type="number"
                                          size="small"
                                          label="Qty"
                                          value={quantity}
                                          onChange={(e) =>
                                            handleCriteriaQuantityChange(
                                              criterionName,
                                              e.target.value
                                            )
                                          }
                                          inputProps={{ min: 1, style: { width: 60 } }}
                                          sx={{ width: 90 }}
                                          disabled={loading}
                                        />
                                      )}
                                    </MDBox>
                                  );
                                })}
                              </MDBox>
                            </MDBox>
                          ))}
                        </>
                      )}

                      {formData.criteria.length > 0 && (
                        <MDBox mt={2} p={2} bgcolor="success.light" borderRadius={2}>
                          <MDTypography variant="body2" color="success.dark" fontWeight="medium">
                            <Icon sx={{ verticalAlign: "middle", mr: 0.5 }}>check_circle</Icon>
                            {formData.criteria.length} criteria selected
                          </MDTypography>
                        </MDBox>
                      )}
                    </MDBox>
                  </Card>
                </Grid>
              </>
            )}

            {/* Step 6: Media Upload */}
            {activeStep === 6 && (
              <>
                {/* Media Upload - Images */}
                <Grid item xs={12}>
                  <Card>
                    <MDBox p={3}>
                      <MDBox
                        display="flex"
                        alignItems="center"
                        mb={3}
                        p={2}
                        borderRadius={2}
                        sx={{
                          flexWrap: "wrap",
                          gap: 1,
                          background:
                            "linear-gradient(135deg, rgba(33, 150, 243, 0.08) 0%, rgba(33, 150, 243, 0.03) 100%)",
                          borderLeft: "4px solid",
                          borderColor: "info.main",
                        }}
                      >
                        <MDBox
                          sx={{
                            width: 40,
                            height: 40,
                            borderRadius: 1.5,
                            bgcolor: "grey.200",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            flexShrink: 0,
                          }}
                        >
                          <Icon sx={{ color: "text.secondary", fontSize: "1.5rem" }}>image</Icon>
                        </MDBox>
                        <MDBox>
                          <MDTypography variant="h6" fontWeight="bold">
                            Property Images
                          </MDTypography>
                          <MDTypography variant="caption" color="text.secondary">
                            Upload property images (Required - Max 3)
                          </MDTypography>
                        </MDBox>
                      </MDBox>

                      <MDBox mb={2}>
                        <input
                          type="file"
                          accept="image/*"
                          multiple
                          onChange={handleImageChange}
                          disabled={loading}
                          style={{ display: "none" }}
                          id="image-upload"
                        />
                        <label htmlFor="image-upload">
                          <MDButton
                            component="span"
                            variant="outlined"
                            color="dark"
                            startIcon={<Icon>add_photo_alternate</Icon>}
                            disabled={loading || images.length >= 3}
                          >
                            Add Images ({images.length}/3)
                          </MDButton>
                        </label>
                        {errors.images && (
                          <MDTypography
                            variant="caption"
                            color="error"
                            ml={1.5}
                            mt={0.5}
                            display="block"
                          >
                            {errors.images}
                          </MDTypography>
                        )}
                      </MDBox>

                      {/* Image Previews */}
                      {imagePreviews.length > 0 && (
                        <Grid container spacing={2}>
                          {imagePreviews.map((preview, index) => (
                            <Grid item xs={6} sm={4} md={3} key={index}>
                              <Paper
                                elevation={2}
                                sx={{
                                  position: "relative",
                                  padding: 1,
                                  borderRadius: 2,
                                }}
                              >
                                <Box
                                  component="img"
                                  src={preview.url}
                                  alt={preview.file}
                                  sx={{
                                    width: "100%",
                                    height: 150,
                                    objectFit: "cover",
                                    borderRadius: 1,
                                  }}
                                />
                                <IconButton
                                  size="small"
                                  onClick={() => removeImage(index)}
                                  disabled={loading}
                                  sx={{
                                    position: "absolute",
                                    top: 8,
                                    right: 8,
                                    bgcolor: "error.main",
                                    color: "white",
                                    "&:hover": { bgcolor: "error.dark" },
                                  }}
                                >
                                  <Icon fontSize="small">close</Icon>
                                </IconButton>
                                <MDTypography
                                  variant="caption"
                                  color="text"
                                  sx={{
                                    display: "block",
                                    mt: 0.5,
                                    overflow: "hidden",
                                    textOverflow: "ellipsis",
                                    whiteSpace: "nowrap",
                                  }}
                                >
                                  {preview.file}
                                </MDTypography>
                              </Paper>
                            </Grid>
                          ))}
                        </Grid>
                      )}
                    </MDBox>
                  </Card>
                </Grid>

                {/* Media Upload - Videos */}
                <Grid item xs={12}>
                  <Card>
                    <MDBox p={3}>
                      <MDBox
                        display="flex"
                        alignItems="center"
                        mb={3}
                        p={2}
                        borderRadius={2}
                        sx={{
                          flexWrap: "wrap",
                          gap: 1,
                          background:
                            "linear-gradient(135deg, rgba(33, 150, 243, 0.08) 0%, rgba(33, 150, 243, 0.03) 100%)",
                          borderLeft: "4px solid",
                          borderColor: "info.main",
                        }}
                      >
                        <MDBox
                          sx={{
                            width: 40,
                            height: 40,
                            borderRadius: 1.5,
                            bgcolor: "grey.200",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            flexShrink: 0,
                          }}
                        >
                          <Icon sx={{ color: "text.secondary", fontSize: "1.5rem" }}>videocam</Icon>
                        </MDBox>
                        <MDBox>
                          <MDTypography variant="h6" fontWeight="bold">
                            Property Videos
                          </MDTypography>
                          <MDTypography variant="caption" color="text.secondary">
                            Upload property videos (Optional - Max 1)
                          </MDTypography>
                        </MDBox>
                      </MDBox>

                      <MDBox mb={2}>
                        <input
                          type="file"
                          accept="video/*"
                          multiple
                          onChange={handleVideoChange}
                          disabled={loading}
                          style={{ display: "none" }}
                          id="video-upload"
                        />
                        <label htmlFor="video-upload">
                          <MDButton
                            component="span"
                            variant="outlined"
                            color="dark"
                            startIcon={<Icon>video_library</Icon>}
                            disabled={loading || videos.length >= 1}
                          >
                            Add Videos ({videos.length}/1)
                          </MDButton>
                        </label>
                        {errors.videos && (
                          <MDTypography
                            variant="caption"
                            color="error"
                            ml={1.5}
                            mt={0.5}
                            display="block"
                          >
                            {errors.videos}
                          </MDTypography>
                        )}
                      </MDBox>

                      {/* Video Previews */}
                      {videoPreviews.length > 0 && (
                        <Grid container spacing={2}>
                          {videoPreviews.map((preview, index) => (
                            <Grid item xs={12} sm={6} md={4} key={index}>
                              <Paper elevation={2} sx={{ p: 1, borderRadius: 2 }}>
                                <Box
                                  component="video"
                                  src={preview.url}
                                  controls
                                  sx={{
                                    width: "100%",
                                    maxHeight: 200,
                                    borderRadius: 1,
                                  }}
                                />
                                <MDBox
                                  display="flex"
                                  justifyContent="space-between"
                                  alignItems="center"
                                  mt={1}
                                >
                                  <MDTypography
                                    variant="caption"
                                    color="text"
                                    sx={{
                                      overflow: "hidden",
                                      textOverflow: "ellipsis",
                                      whiteSpace: "nowrap",
                                      flex: 1,
                                    }}
                                  >
                                    {preview.file}
                                  </MDTypography>
                                  <IconButton
                                    size="small"
                                    onClick={() => removeVideo(index)}
                                    disabled={loading}
                                    color="error"
                                  >
                                    <Icon fontSize="small">delete</Icon>
                                  </IconButton>
                                </MDBox>
                              </Paper>
                            </Grid>
                          ))}
                        </Grid>
                      )}
                    </MDBox>
                  </Card>
                </Grid>

                {/* Media Upload - Building Images */}
                <Grid item xs={12}>
                  <Card>
                    <MDBox p={3}>
                      <MDBox
                        display="flex"
                        alignItems="center"
                        mb={3}
                        p={2}
                        borderRadius={2}
                        sx={{
                          flexWrap: "wrap",
                          gap: 1,
                          background:
                            "linear-gradient(135deg, rgba(33, 150, 243, 0.08) 0%, rgba(33, 150, 243, 0.03) 100%)",
                          borderLeft: "4px solid",
                          borderColor: "info.main",
                        }}
                      >
                        <MDBox
                          sx={{
                            width: 40,
                            height: 40,
                            borderRadius: 1.5,
                            bgcolor: "grey.200",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            flexShrink: 0,
                          }}
                        >
                          <Icon sx={{ color: "text.secondary", fontSize: "1.5rem" }}>business</Icon>
                        </MDBox>
                        <MDBox>
                          <MDTypography variant="h6" fontWeight="bold">
                            Building Images (Outside)
                          </MDTypography>
                          <MDTypography variant="caption" color="text.secondary">
                            Upload image of the building from outside (Optional - Max 1)
                          </MDTypography>
                        </MDBox>
                      </MDBox>

                      <MDBox mb={2}>
                        <input
                          type="file"
                          accept="image/*"
                          multiple
                          onChange={handleBuildingImageChange}
                          disabled={loading}
                          style={{ display: "none" }}
                          id="building-image-upload"
                        />
                        <label htmlFor="building-image-upload">
                          <MDButton
                            component="span"
                            variant="outlined"
                            color="dark"
                            startIcon={<Icon>add_photo_alternate</Icon>}
                            disabled={loading || buildingImages.length >= 1}
                          >
                            Add Building Images ({buildingImages.length}/1)
                          </MDButton>
                        </label>
                        {errors.buildingImages && (
                          <MDTypography
                            variant="caption"
                            color="error"
                            ml={1.5}
                            mt={0.5}
                            display="block"
                          >
                            {errors.buildingImages}
                          </MDTypography>
                        )}
                      </MDBox>

                      {/* Building Image Previews */}
                      {buildingImagePreviews.length > 0 && (
                        <Grid container spacing={2}>
                          {buildingImagePreviews.map((preview, index) => (
                            <Grid item xs={12} sm={6} md={4} key={index}>
                              <Paper
                                elevation={2}
                                sx={{ p: 1, borderRadius: 2, position: "relative" }}
                              >
                                <Box
                                  component="img"
                                  src={preview.url}
                                  alt={preview.file}
                                  sx={{
                                    width: "100%",
                                    height: 200,
                                    objectFit: "cover",
                                    borderRadius: 1,
                                  }}
                                />
                                <MDBox
                                  sx={{
                                    position: "absolute",
                                    top: 8,
                                    right: 8,
                                  }}
                                >
                                  <IconButton
                                    size="small"
                                    onClick={() => removeBuildingImage(index)}
                                    disabled={loading}
                                    sx={{
                                      bgcolor: "rgba(255, 255, 255, 0.9)",
                                      "&:hover": {
                                        bgcolor: "rgba(255, 255, 255, 1)",
                                      },
                                    }}
                                    color="error"
                                  >
                                    <Icon fontSize="small">delete</Icon>
                                  </IconButton>
                                </MDBox>
                              </Paper>
                            </Grid>
                          ))}
                        </Grid>
                      )}
                    </MDBox>
                  </Card>
                </Grid>
              </>
            )}

            {/* Step 7: Legal Papers */}
            {activeStep === 7 && (
              <>
                <Grid item xs={12}>
                  <Card>
                    <MDBox p={3}>
                      <MDBox
                        display="flex"
                        alignItems="center"
                        mb={3}
                        p={2}
                        borderRadius={2}
                        sx={{
                          flexWrap: "wrap",
                          gap: 1,
                          background:
                            "linear-gradient(135deg, rgba(33, 150, 243, 0.08) 0%, rgba(33, 150, 243, 0.03) 100%)",
                          borderLeft: "4px solid",
                          borderColor: "info.main",
                        }}
                      >
                        <MDBox
                          sx={{
                            width: 40,
                            height: 40,
                            borderRadius: 1.5,
                            bgcolor: "grey.200",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            flexShrink: 0,
                          }}
                        >
                          <Icon sx={{ color: "text.secondary", fontSize: "1.5rem" }}>
                            description
                          </Icon>
                        </MDBox>
                        <MDBox>
                          <MDTypography variant="h6" fontWeight="bold">
                            Legal Papers
                          </MDTypography>
                          <MDTypography variant="caption" color="text.secondary">
                            Upload required legal documents in PDF format
                          </MDTypography>
                        </MDBox>
                      </MDBox>

                      <Grid container spacing={3}>
                        {/* Property Owner ID - Required */}
                        <Grid item xs={12} md={6}>
                          <MDBox mb={2}>
                            <MDTypography variant="body2" fontWeight="bold" mb={1}>
                              Property Owner ID <span style={{ color: "red" }}>*</span>
                            </MDTypography>
                            <input
                              type="file"
                              accept="application/pdf"
                              onChange={handlePropertyOwnerIdChange}
                              disabled={loading}
                              style={{ display: "none" }}
                              id="property-owner-id-upload"
                            />
                            <label htmlFor="property-owner-id-upload">
                              <MDBox
                                sx={{
                                  border: "2px dashed",
                                  borderColor: propertyOwnerId
                                    ? "success.main"
                                    : errors.propertyOwnerId
                                    ? "error.main"
                                    : "grey.300",
                                  borderRadius: 2,
                                  p: 2,
                                  textAlign: "center",
                                  bgcolor: propertyOwnerId ? "success.lighter" : "grey.50",
                                  cursor: "pointer",
                                  transition: "all 0.3s",
                                  "&:hover": {
                                    borderColor: "info.main",
                                    bgcolor: "info.lighter",
                                  },
                                }}
                              >
                                {propertyOwnerId ? (
                                  <MDBox>
                                    <Icon sx={{ fontSize: "2rem", color: "success.main", mb: 1 }}>
                                      picture_as_pdf
                                    </Icon>
                                    <MDTypography variant="body2" fontWeight="medium" mb={0.5}>
                                      {propertyOwnerId.name}
                                    </MDTypography>
                                    <MDTypography
                                      variant="caption"
                                      color="text.secondary"
                                      mb={1}
                                      display="block"
                                    >
                                      {(propertyOwnerId.size / 1024).toFixed(2)} KB
                                    </MDTypography>
                                    <MDButton
                                      size="small"
                                      color="error"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        setPropertyOwnerId(null);
                                        setPropertyOwnerIdPreview(null);
                                      }}
                                    >
                                      Remove
                                    </MDButton>
                                  </MDBox>
                                ) : (
                                  <MDBox>
                                    <Icon sx={{ fontSize: "2rem", color: "text.secondary", mb: 1 }}>
                                      upload_file
                                    </Icon>
                                    <MDTypography variant="body2" color="text.secondary">
                                      Click to upload Property Owner ID (PDF)
                                    </MDTypography>
                                  </MDBox>
                                )}
                              </MDBox>
                            </label>
                            {errors.propertyOwnerId && (
                              <MDTypography
                                variant="caption"
                                color="error"
                                mt={0.5}
                                display="block"
                              >
                                {errors.propertyOwnerId}
                              </MDTypography>
                            )}
                          </MDBox>
                        </Grid>

                        {/* Rental Contract or Ownership Contract - Required */}
                        <Grid item xs={12} md={6}>
                          <MDBox mb={2}>
                            <MDTypography variant="body2" fontWeight="bold" mb={1}>
                              Rental Contract or Ownership Contract{" "}
                              <span style={{ color: "red" }}>*</span>
                            </MDTypography>
                            <input
                              type="file"
                              accept="application/pdf"
                              onChange={handleRentalOrOwnershipContractChange}
                              disabled={loading}
                              style={{ display: "none" }}
                              id="rental-ownership-contract-upload"
                            />
                            <label htmlFor="rental-ownership-contract-upload">
                              <MDBox
                                sx={{
                                  border: "2px dashed",
                                  borderColor: rentalOrOwnershipContract
                                    ? "success.main"
                                    : errors.rentalOrOwnershipContract
                                    ? "error.main"
                                    : "grey.300",
                                  borderRadius: 2,
                                  p: 2,
                                  textAlign: "center",
                                  bgcolor: rentalOrOwnershipContract
                                    ? "success.lighter"
                                    : "grey.50",
                                  cursor: "pointer",
                                  transition: "all 0.3s",
                                  "&:hover": {
                                    borderColor: "info.main",
                                    bgcolor: "info.lighter",
                                  },
                                }}
                              >
                                {rentalOrOwnershipContract ? (
                                  <MDBox>
                                    <Icon sx={{ fontSize: "2rem", color: "success.main", mb: 1 }}>
                                      picture_as_pdf
                                    </Icon>
                                    <MDTypography variant="body2" fontWeight="medium" mb={0.5}>
                                      {rentalOrOwnershipContract.name}
                                    </MDTypography>
                                    <MDTypography
                                      variant="caption"
                                      color="text.secondary"
                                      mb={1}
                                      display="block"
                                    >
                                      {(rentalOrOwnershipContract.size / 1024).toFixed(2)} KB
                                    </MDTypography>
                                    <MDButton
                                      size="small"
                                      color="error"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        setRentalOrOwnershipContract(null);
                                        setRentalOrOwnershipContractPreview(null);
                                      }}
                                    >
                                      Remove
                                    </MDButton>
                                  </MDBox>
                                ) : (
                                  <MDBox>
                                    <Icon sx={{ fontSize: "2rem", color: "text.secondary", mb: 1 }}>
                                      upload_file
                                    </Icon>
                                    <MDTypography variant="body2" color="text.secondary">
                                      Click to upload Contract (PDF)
                                    </MDTypography>
                                  </MDBox>
                                )}
                              </MDBox>
                            </label>
                            {errors.rentalOrOwnershipContract && (
                              <MDTypography
                                variant="caption"
                                color="error"
                                mt={0.5}
                                display="block"
                              >
                                {errors.rentalOrOwnershipContract}
                              </MDTypography>
                            )}
                          </MDBox>
                        </Grid>

                        {/* Power of Attorney - Optional */}
                        <Grid item xs={12} md={6}>
                          <MDBox mb={2}>
                            <MDTypography variant="body2" fontWeight="bold" mb={1}>
                              Power of Attorney <span style={{ color: "grey" }}>(Optional)</span>
                            </MDTypography>
                            <input
                              type="file"
                              accept="application/pdf"
                              onChange={handlePowerOfAttorneyChange}
                              disabled={loading}
                              style={{ display: "none" }}
                              id="power-of-attorney-upload"
                            />
                            <label htmlFor="power-of-attorney-upload">
                              <MDBox
                                sx={{
                                  border: "2px dashed",
                                  borderColor: powerOfAttorney
                                    ? "success.main"
                                    : errors.powerOfAttorney
                                    ? "error.main"
                                    : "grey.300",
                                  borderRadius: 2,
                                  p: 2,
                                  textAlign: "center",
                                  bgcolor: powerOfAttorney ? "success.lighter" : "grey.50",
                                  cursor: "pointer",
                                  transition: "all 0.3s",
                                  "&:hover": {
                                    borderColor: "info.main",
                                    bgcolor: "info.lighter",
                                  },
                                }}
                              >
                                {powerOfAttorney ? (
                                  <MDBox>
                                    <Icon sx={{ fontSize: "2rem", color: "success.main", mb: 1 }}>
                                      picture_as_pdf
                                    </Icon>
                                    <MDTypography variant="body2" fontWeight="medium" mb={0.5}>
                                      {powerOfAttorney.name}
                                    </MDTypography>
                                    <MDTypography
                                      variant="caption"
                                      color="text.secondary"
                                      mb={1}
                                      display="block"
                                    >
                                      {(powerOfAttorney.size / 1024).toFixed(2)} KB
                                    </MDTypography>
                                    <MDButton
                                      size="small"
                                      color="error"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        setPowerOfAttorney(null);
                                        setPowerOfAttorneyPreview(null);
                                      }}
                                    >
                                      Remove
                                    </MDButton>
                                  </MDBox>
                                ) : (
                                  <MDBox>
                                    <Icon sx={{ fontSize: "2rem", color: "text.secondary", mb: 1 }}>
                                      upload_file
                                    </Icon>
                                    <MDTypography variant="body2" color="text.secondary">
                                      Click to upload Power of Attorney (PDF)
                                    </MDTypography>
                                  </MDBox>
                                )}
                              </MDBox>
                            </label>
                            {errors.powerOfAttorney && (
                              <MDTypography
                                variant="caption"
                                color="error"
                                mt={0.5}
                                display="block"
                              >
                                {errors.powerOfAttorney}
                              </MDTypography>
                            )}
                          </MDBox>
                        </Grid>
                      </Grid>
                    </MDBox>
                  </Card>
                </Grid>
              </>
            )}

            {/* Submit Button - Navigation and Submit */}
            <Grid item xs={12}>
              <Card sx={{ p: { xs: 2, sm: 3 }, bgcolor: "grey.50" }}>
                <MDBox
                  display="flex"
                  justifyContent="space-between"
                  alignItems="center"
                  flexWrap="wrap"
                  gap={2}
                  sx={{
                    flexDirection: { xs: "column", sm: "row" },
                    alignItems: { xs: "stretch", sm: "center" },
                  }}
                >
                  <MDBox>
                    {activeStep === steps.length - 1 && (
                      <>
                        <MDTypography variant="h6" fontWeight="bold" mb={0.5}>
                          Ready to Submit?
                        </MDTypography>
                        <MDTypography variant="body2" color="text.secondary">
                          Review all information before submitting. Your property will be reviewed
                          by our team.
                        </MDTypography>
                      </>
                    )}
                  </MDBox>
                  <MDBox
                    display="flex"
                    gap={2}
                    flexWrap="wrap"
                    justifyContent="space-between"
                    sx={{ flexDirection: { xs: "column", sm: "row" } }}
                  >
                    <MDBox
                      display="flex"
                      gap={2}
                      flexWrap="wrap"
                      sx={{ "& .MuiButton-root": { minWidth: { xs: "100%", sm: "auto" } } }}
                    >
                      {activeStep > 0 && (
                        <MDButton
                          variant="outlined"
                          color="secondary"
                          onClick={handleBack}
                          disabled={loading}
                          startIcon={<Icon>arrow_back</Icon>}
                          fullWidth
                          sx={{ display: { xs: "flex", sm: "inline-flex" } }}
                        >
                          Back
                        </MDButton>
                      )}
                      {activeStep < steps.length - 1 && (
                        <MDButton
                          variant="gradient"
                          color="info"
                          onClick={handleNext}
                          disabled={loading}
                          endIcon={<Icon>arrow_forward</Icon>}
                          fullWidth
                          sx={{ display: { xs: "flex", sm: "inline-flex" } }}
                        >
                          Next
                        </MDButton>
                      )}
                    </MDBox>
                    <MDBox
                      display="flex"
                      gap={2}
                      flexWrap="wrap"
                      sx={{ "& .MuiButton-root": { minWidth: { xs: "100%", sm: "auto" } } }}
                    >
                      <MDButton
                        variant="outlined"
                        color="secondary"
                        onClick={() => navigate("/property-owner/properties")}
                        disabled={loading}
                        startIcon={<Icon>close</Icon>}
                        fullWidth
                        sx={{ display: { xs: "flex", sm: "inline-flex" } }}
                      >
                        Cancel
                      </MDButton>
                      {activeStep === steps.length - 1 && (
                        <MDButton
                          type="submit"
                          variant="gradient"
                          color="info"
                          disabled={loading}
                          startIcon={
                            loading ? (
                              <CircularProgress size={16} color="inherit" />
                            ) : (
                              <Icon>check_circle</Icon>
                            )
                          }
                          fullWidth
                          sx={{
                            display: { xs: "flex", sm: "inline-flex" },
                            minWidth: { xs: "100%", sm: 180 },
                            boxShadow: loading ? "none" : "0 4px 12px rgba(33, 150, 243, 0.4)",
                            "&:hover": {
                              boxShadow: "0 6px 16px rgba(33, 150, 243, 0.5)",
                            },
                          }}
                        >
                          {loading ? "Submitting..." : "Submit Property"}
                        </MDButton>
                      )}
                    </MDBox>
                  </MDBox>
                </MDBox>
              </Card>
            </Grid>
          </Grid>
        </form>
      </MDBox>
      <PropertyOwnerFooter />
    </DashboardLayout>
  );
}

export default AddProperty;
